<script lang="ts">
	// What a signed-in till shows: the header, the sell screen or today's
	// orders, the sheets that open over them, and the lock over all of it.
	//
	// A task sits in a Sheet above the screen it belongs to: the line editor,
	// the delivery details, and payment, which becomes Done once the sale has
	// gone through. Every order is sent before it is paid (#348): Send on Sell
	// places it open or adds to it, Pay takes the payment of the sent order,
	// and Orders opens an open order back on Sell. The screen beneath stays mounted and the till keeps what
	// each sheet was holding, so closing one never discards what was typed.
	//
	// The lock is an overlay with `inert` beneath it, and it never unmounts
	// what it covers: a cashier who unlocks comes back to the sheet and the
	// draft they had open. The Discount sheet is the one exception: it closes
	// on a lock, so the next cashier never finds a manager's half-typed
	// discount (UC-35). So does Pick a table (UC-39), whose cart keeps the
	// table it had. So do Cancel item and Manager approval (#379), with the
	// order as it was: an approval is a manager's, for the cashier in front
	// of it, and never waits half-typed for the next one.
	//
	// Only a manager voids an order or cancels a sent line (#379). A manager
	// signed in does it at once; anyone else gets Manager approval, which
	// names the one operation and takes a manager's name and passcode. The
	// till's order store sends the digits with the operation, and the
	// cashier stays signed in.

	import { openDrawerNoSale, openDrawerNow, retryDrawer, watchDrawer } from '../till/drawer';
	import Button from '../components/Button.svelte';
	import BrandMark from '../components/BrandMark.svelte';
	import Notice from '../components/Notice.svelte';
	import Sheet from '../components/Sheet.svelte';
	import Segmented from '../components/Segmented.svelte';
	import {
		languageChoice,
		setLanguageChoice,
		setThemeChoice,
		themeChoice,
		type ThemeChoice,
	} from '../display.svelte';
	import { untrack } from 'svelte';
	import { SvelteSet } from 'svelte/reactivity';
	import { fill, message, type Language } from '../i18n/messages';
	import { api } from '../api/client';
	import {
		askAtLock,
		askWhileAtRest,
		installNowAsks,
		lockedTillAtRest,
		ownLockAtRest,
		tillAsk,
		tillAtRest,
		type Writes,
	} from '../till/rest';
	import { switchAtRest } from '../till/shell';
	import { INSTALL_NOW_ASK_MS, InstallNow, installWhenQuiet } from '../till/appUpdate.svelte';
	import { notReadyCode } from '../till/answer';
	import { discountRefusalMessage } from '../till/discountRefusal';
	import { discountTaken } from '../format/discount';
	import { formatMinor, parseTypedMinor } from '../format/money';
	import { change } from '../domain/change';
	import { idleFor, watchActivity } from '../till/idle.svelte';
	import {
		askIdleLock,
		connectionLine,
		linkUp,
		readStoreState,
		shownWaiting,
	} from '../till/storeState.svelte';
	import { opened } from '../till/session.svelte';
	import { openCount, refreshOpenCount, watchOpenCount } from '../till/openCount.svelte';
	import {
		currentPicks,
		forgetMyItems,
		loadMyItems,
		myItems,
		saveMyItems,
		setPicks,
	} from '../till/mine.svelte';
	import { PasscodeEntry } from '../till/passcode.svelte';
	import {
		blocked,
		pickedModifiers,
		unitOf,
		baseOf,
		workForNew,
		workForWeighed,
		workFromLine,
		type EditorWork,
	} from '../till/editor';
	import { lineName, soldByWeight } from '../till/weighed';
	import Sell from '../panels/Sell.svelte';
	import MyItems from '../panels/MyItems.svelte';
	import LineEditor from '../panels/LineEditor.svelte';
	import { noteShortcuts } from '../till/noteShortcuts';
	import Delivery from '../panels/Delivery.svelte';
	import CustomerSheet from '../panels/CustomerSheet.svelte';
	import Shift from '../panels/Shift.svelte';
	import OpenShift from '../panels/OpenShift.svelte';
	import CashMovement from '../panels/CashMovement.svelte';
	import CloseShift from '../panels/CloseShift.svelte';
	import { payNotice, shiftRefusal, shiftState, voidRefusal } from '../till/shift.svelte';
	import { forgetMovementDrawer } from '../till/cashMovement.svelte';
	import Payment, { type PayWay } from '../panels/Payment.svelte';
	import PickTable from '../panels/PickTable.svelte';
	import {
		cartTable,
		currentTables,
		loadTables,
		shopHasTables,
		tableNeeded,
		tablesRead,
		type Table,
	} from '../till/tables.svelte';
	import PickDriver from '../panels/PickDriver.svelte';
	import {
		cartDriver,
		currentDrivers,
		driverNeeded,
		driversRead,
		loadDrivers,
		type Driver,
	} from '../till/drivers.svelte';
	import Done, { type PrintPhase } from '../panels/Done.svelte';
	import GiveDiscount from '../panels/GiveDiscount.svelte';
	import Orders from '../panels/Orders.svelte';
	import CancelLine from '../panels/CancelLine.svelte';
	import ManagerApproval from '../panels/ManagerApproval.svelte';
	import { Roster } from '../till/roster.svelte';
	import TillSettings from '../panels/TillSettings.svelte';
	import TillDriversPanel from '../panels/TillDrivers.svelte';
	import SetUpTill from '../panels/SetUpTill.svelte';
	import SharingStep from '../panels/SharingStep.svelte';
	import CashPointStep from '../panels/CashPointStep.svelte';
	import { TillMesh } from '../till/mesh.svelte';
	import { TillCashPoint } from '../till/cashPoint.svelte';
	import { TillDrivers } from '../till/tillDrivers.svelte';
	import { SETUP_READ_AGAIN_MS, SETUP_READ_TRIES, TillSetup } from '../till/setup.svelte';
	import {
		isLastOpenStep,
		knownSteps,
		nextOpenIndex,
		onboardingIndex,
		setupShows,
		shouldOpenSetup,
	} from '../till/setupSteps';
	import Printers from '../panels/Printers.svelte';
	import PrinterSetup from '../panels/PrinterSetup.svelte';
	import PrinterIdentify from '../panels/PrinterIdentify.svelte';
	import {
		activateLocalSetup,
		identifyingIds,
		loadMesh,
		decideHeld,
		decisionsPending,
		heldJobs,
		loadPrinters,
		meshCapabilities,
		meshSharing,
		meshSetup,
		notAnsweringPurposes,
		printers,
		printNoticeAgain,
		printNotices,
		receiptPrinter,
		printersRead,
		printNoticeBusy,
		refreshPrintJobs,
		startPrintWatch,
		forgetPrinting,
		stopPrintWatch,
		type Printer,
		type PrintNotice,
	} from '../till/printers.svelte';
	import LockPad from '../panels/LockPad.svelte';
	import {
		addLine,
		attachDraft,
		clearDraft,
		currentDraft,
		currentPending,
		draftTotals,
		endCheckout,
		noteDeliveryFee,
		openOrder as goOnWith,
		removeLine,
		setDelivery,
		setDiscount,
		setTable,
		setDriver,
		updateLine,
		type DraftLine,
	} from '../draft/draft.svelte';
	import type { OrderDiscount } from '../domain/pricing';
	import {
		MENU_POLL_MS,
		addToOrder,
		additionPending,
		changeOrderDiscount,
		currentOpenOrder,
		currentTradingDay,
		loadOpenOrder,
		showOpenOrder,
		type AddOutcome,
		checkout,
		currentMenu,
		hasMenu,
		currentQuotedFee,
		currentAreas,
		discountRemoved,
		forgetRemoved,
		undoLineRemoval,
		isOnline,
		loadMenu,
		loadAreas,
		loadDay,
		loadSends,
		SENDS_POLL_MS,
		type Sends,
		payOrder,
		quoteDelivery,
		resumeCheckout,
		runApproved,
		newWriteKey,
		approvalResult,
		type ApprovedOperation,
		type ApprovedOutcome,
		cardNeedsManager,
		type ReasonKey,
		type CheckoutOutcome,
		type Day,
	} from '../till/sell.svelte';
	import { queueBill, waitForBill } from '../till/printing';
	import { queuePrint, waitForReceipt, PRINT_COOLDOWN_MS } from '../till/printing';
	import { heldChoices, type DecideOutcome, type Decision, type PrintJob } from '../till/printing';
	import { orderNumber, tillOrderNumber } from '../format/order';
	import {
		addressRemovalUnsettled,
		commitAddressRemoval,
		knownCustomer,
		pendingAddressRemoval,
		undoAddressRemoval,
	} from '../till/customers.svelte';
	import {
		confirmDelivery,
		customerGone,
		deliverySteps,
		openDeliverySteps,
		pickedPhone,
		startNewAddress,
		startNewCustomer,
		stepBack,
		stepContext,
	} from '../till/delivery.svelte';
	import { addressArea, canStartNew, pickedAddress, stepValues } from '../till/deliverySteps';
	import type { components } from '../api/types';
	import type { components as storeComponents } from '../api/store';

	type Identity = components['schemas']['TillIdentityBody'];
	type Session = components['schemas']['SessionBody'];
	type Item = components['schemas']['ItemBody'];
	type Order = storeComponents['schemas']['OrderBody'];
	type PaidOrder = Pick<
		components['schemas']['OrderBody'],
		'status' | 'totalMinor' | 'discount' | 'discountMinor'
	>;

	interface Props {
		language: Language;
		till: Identity;
		session: Session;
		idleSeconds: number;
		onSignedOut: () => void;
	}

	let { language, till, session, idleSeconds, onSignedOut }: Props = $props();

	// The server's answer is the starting point and nothing more: from here the
	// till owns the lock, because it is the till that watches for idleness and
	// the till that takes the passcode.
	let locked = $state(untrack(() => session.locked));
	const entry = new PasscodeEntry((next) => {
		locked = false;
		// Any cashier of the shop may clear the lock, so whoever typed is now
		// the one signed in: their role decides what they may correct. Unlocking
		// is also when a till catches up with its account's settings.
		opened(next);
		void readManagerSetup(next);
		// And opens on the cashier's own items (UC-17), read again because the
		// passcode may have been another cashier's.
		rail = 'mine';
		void loadMyItems(next.userId ?? '');
	});
	$effect(() => () => entry.stop());

	// The lock over the till has its own cashier already picked, so they carry
	// on with eight digits; anyone else taps "Not you?" and picks their name.
	function pickSignedIn() {
		entry.pick({ id: session.userId ?? '', name: session.userName, role: session.role });
	}
	untrack(() => {
		if (session.locked) pickSignedIn();
	});

	// The screen under the sheets: selling, today's orders, printers, the
	// till's shift (T29), or Till settings (T26). Back on Till settings
	// returns where it came from, and T25's Back where T25 came from: Orders,
	// Till settings, or the onboarding's printers step, which is Sell with the
	// Sheet over it.
	let view = $state<'sell' | 'orders' | 'printers' | 'shift' | 'settings'>('sell');
	// The shift's two sheets (UC-60): Open shift (T30), over the screen in
	// front or over Payment, and Close shift (T31), over Shift. While the till
	// takes no payment, the standing Notice says why under the header and on
	// Payment, with Open shift on a till that is its own cash point.
	let openShiftOpen = $state(false);
	let closeShiftOpen = $state(false);
	// Cash out / in (T35, UC-67), over Shift.
	let cashMoveOpen = $state(false);
	// Each sheet keeps what was typed on it when it closes, a lock included,
	// and says so here, so the till's own lock installs nothing over it (#925).
	let openShiftHolding = $state(false);
	let closeShiftHolding = $state(false);
	let cashMoveHolding = $state(false);
	const shiftNotice = $derived(payNotice(language, shiftState()));

	let settingsFrom = $state<'sell' | 'orders' | 'printers' | 'shift'>('sell');
	let printersFrom = $state<'orders' | 'settings' | 'onboarding'>('orders');
	// Where T25 returns once Till settings, opened over T25, goes back to it.
	let settingsPrintersFrom = $state<'orders' | 'settings' | 'onboarding'>('orders');

	// Till settings and the onboarding (T27) share one read of the till's
	// setup, which its order store keeps (UC-59, #774). The onboarding stands
	// at one known step, and opens for the manager whose sign-in read it.
	const tillSetup = new TillSetup();
	let setupIndex = $state(0);
	let setupUserId = $state<string | null>(null);
	// Who closed the onboarding in this session. Another person's unlock
	// starts a session of their own (UC-1), so it clears the Close.
	let setupClosedFor = $state<string | null>(null);
	let setupRead = 0;
	// The sharing step (T28, #775) reads the till mesh, on a Windows till
	// whose store lists the step; Till settings' Sharing row opens it as a
	// Sheet over Till settings.
	const tillMesh = new TillMesh();
	let sharingOpen = $state(false);
	// The cash-point step (T32, #792) reads where the till's cash goes, for
	// its onboarding page and Till settings' Cash point row, which opens it
	// as a Sheet over Till settings whose Confirm sends the pick. It reads
	// the mesh the step reads, for the tills on an older app.
	const tillCashPoint = new TillCashPoint(tillMesh);
	let cashPointOpen = $state(false);
	// A manager's Drivers (T34, #1003): Till settings' Drivers row says how
	// many, and opens the list as a Sheet over Till settings. The list is the
	// server's, read through the store's forward, or from the list the store
	// keeps while Bosla does not answer.
	const tillDrivers = new TillDrivers();
	let driversOpen = $state(false);
	// Whose session read the drivers: another person's unlock forgets them.
	let driversFor = '';

	// A session starts on this till: a manager's reads the setup and opens the
	// onboarding at its first step not passed; a cashier's reads nothing, as
	// the store would refuse it.
	async function readManagerSetup(next: Session) {
		const mine = ++setupRead;
		setupUserId = null;
		sharingOpen = false;
		cashPointOpen = false;
		tillCashPoint.reset();
		if (driversFor !== (next.userId ?? '')) {
			driversOpen = false;
			tillDrivers.reset();
			driversFor = next.userId ?? '';
		}
		if (setupClosedFor !== (next.userId ?? '')) setupClosedFor = null;
		// Till settings and Printers are everyone's: a cashier who unlocks over
		// either keeps it, and reads no setup (T26 shows them Language and
		// theme alone).
		if (next.role !== 'manager') return;
		tillSetup.failure = false;
		// A read the store did not answer, or answered 5xx, is asked again a
		// few times, so one lost answer does not hide the onboarding.
		for (let tries = 1; ; tries++) {
			await tillSetup.read();
			if (mine !== setupRead) return;
			if (!tillSetup.retryable || tries >= SETUP_READ_TRIES) break;
			await new Promise((resolve) => setTimeout(resolve, SETUP_READ_AGAIN_MS));
			if (mine !== setupRead) return;
		}
		tillSetup.failure = false;
		setupIndex = Math.max(0, onboardingIndex(tillSetup.setup));
		setupUserId = next.userId ?? '';
	}

	// The screen starts with a session open: a sign-in on the lock screen, or
	// a reload. A locked one waits for its unlock, above.
	$effect(() => {
		untrack(() => {
			if (!locked) void readManagerSetup(session);
		});
	});

	// Till settings' Printers row and the onboarding's printers step say how
	// many printers the till has, so each reads them as it shows; the
	// onboarding waits for that read before it opens.
	$effect(() => {
		const atPrinters =
			setupUserId !== null &&
			shouldOpenSetup(tillSetup.setup) &&
			knownSteps(tillSetup.setup)[setupIndex]?.id === 'printers';
		if ((isManager && view === 'settings') || (view === 'sell' && atPrinters)) {
			untrack(() => void loadPrinters());
		}
	});

	// Till settings' Cash point row and the onboarding's cash-point step say
	// where the till's cash goes. A manager's Till settings reads it as it
	// opens on a store that lists the step, and a pending onboarding whose
	// cash-point step has not passed reads it at once, before the manager
	// reaches the step, which says it waits while the read is on its way.
	$effect(() => {
		const steps = knownSteps(tillSetup.setup);
		const listsCashPoint = isManager && steps.some((step) => step.id === 'cash_point');
		const awaits =
			setupUserId !== null &&
			shouldOpenSetup(tillSetup.setup) &&
			steps.some((step) => step.id === 'cash_point' && step.passed === undefined);
		if ((view === 'settings' && listsCashPoint) || (view === 'sell' && awaits)) {
			untrack(() => void tillCashPoint.read());
		}
	});

	function closeCashPoint() {
		cashPointOpen = false;
		tillCashPoint.reset();
	}

	// Confirm on T32 over Till settings sends the pick, then closes the Sheet;
	// a refusal stays on it in words.
	async function confirmCashPoint() {
		if (await tillCashPoint.save()) cashPointOpen = false;
	}

	function openSetupPrinters(from: 'settings' | 'onboarding') {
		printersFrom = from;
		view = 'printers';
	}

	// Skip, Next or Confirm: the store keeps the pass, then the onboarding
	// moves on, or after its last step signs the manager out as Sign out does.
	// A pass that did not go through keeps the step.
	async function passSetup(passed: 'done' | 'skipped') {
		const steps = knownSteps(tillSetup.setup);
		const step = steps[setupIndex];
		if (!step || tillSetup.saving) return;
		// The last step to pass may not be the last shown: an older screen can
		// leave sharing pending on a Windows till whose printers passed.
		const last = isLastOpenStep(tillSetup.setup, setupIndex);
		const at = setupIndex;
		const mine = setupRead;
		// The cash-point step's Next or Confirm first sends the pick; a
		// refusal keeps the step, saying why. Skip keeps the kept cash point.
		if (step.id === 'cash_point') {
			if (passed === 'skipped') tillCashPoint.reset();
			else if (!(await tillCashPoint.save()) || mine !== setupRead) return;
		}
		if (!(await tillSetup.pass(step.id, passed)) || mine !== setupRead) return;
		if (last || tillSetup.setup?.onboarding === 'finished') await signOut();
		else setupIndex = Math.max(at + 1, nextOpenIndex(tillSetup.setup, at));
	}

	// The printer setup sheet (T25), shown only on the printers view.
	let printerSetupOpen = $state(false);
	let printerSetupMode = $state<'add' | 'again' | 'change'>('add');
	let printerSetupTarget = $state<Printer | null>(null);
	let printerIdentifyOpen = $state(false);
	let printerIdentifyTarget = $state<Printer | null>(null);

	// The sell rail's chosen entry: 'mine' or a category id. A till opens on
	// the cashier's own items, and so does every unlock.
	let rail = $state('mine');

	// The "Edit my items" sheet (UC-17).
	let mineOpen = $state(false);
	let mineSaving = $state(false);
	let mineError = $state('');

	// The sheet on top, if any. Payment turns into Done once the sale is
	// through, in the same sheet, so the cashier's eyes stay where they were.
	// `number` is the order's number as the till reads it: its prefix-N, A-12.
	type Collect = {
		orderId: string;
		number: string;
		totalMinor: number;
		discounted: boolean;
		/** A card payment of it needs a manager (UC-16.R9): a driver's sent delivery. */
		cardNeedsManager: boolean;
	};
	type Settled = {
		method: 'cash' | 'card';
		changeMinor: number;
		totalMinor: number;
		number: string;
		orderId: string;
		drawerJobId?: string;
		/** An order its discount made free (UC-35), named for the Done screen. */
		free?: { discount: string } | null;
	};
	type Open =
		| { name: 'editor' }
		| { name: 'delivery' }
		| { name: 'pay'; collect: Collect | null }
		| { name: 'done'; settled: Settled };
	let sheet = $state<Open | null>(null);

	// The till's own language and theme, chosen in a sheet from the header.
	let displayOpen = $state(false);

	// The Discount sheet (UC-35), over Sell.
	let discountOpen = $state(false);

	// Cancel item (UC-44, #379), over Sell: the sent line it cancels part or
	// all of, the reason in flight while a manager's own cancellation is
	// written, and why the last one did not go through.
	type SentLine = components['schemas']['QuotedLineBody'];
	let cancelling = $state<SentLine | null>(null);
	let cancelSaving = $state<ReasonKey | null>(null);
	let cancelFailure = $state('');
	// Each opening of Cancel item: its count, which starts the sheet at one,
	// and the base of the keys its choices are sent under.
	let cancelOpened = $state(0);
	let cancelKeyBase = '';
	// Manager approval (#379), over Sell or Orders: the one operation it
	// approves, its title, and the key that names it, the same for every try
	// at it, so a try whose answer was lost is answered as before.
	let asking = $state<{ op: ApprovedOperation; title: string; key: string } | null>(null);
	const approvers = new Roster();
	const approval = new PasscodeEntry<Order>(
		(order) => approved(order),
		async (userId, passcode) => {
			const ask = asking;
			if (ask === null) return { ok: false, failure: 'refused' };
			const outcome = await runApproved(ask.op, ask.key, { userId, passcode });
			const result = approvalResult(outcome);
			if (
				ask.op.kind === 'driver' &&
				outcome.kind === 'refused' &&
				!result.ok &&
				result.failure === 'refused'
			) {
				// The approval is not the problem: Pick a driver says what is.
				driverFailure = driverChangeFailure(outcome.code, outcome.details?.path);
				asking = null;
				if (driverClosedCode(outcome.code)) void refreshDay();
			}
			if (ask.op.kind === 'pay') {
				// The approved card payment: Done on its answer, or Payment says
				// what refused it when the approval is not the problem.
				const target = sheet?.name === 'pay' ? sheet.collect : null;
				if (outcome.kind === 'done' && target) {
					asking = null;
					approval.unpick();
					paidByCard(target, outcome);
				} else if (!result.ok && result.failure === 'refused' && target) {
					asking = null;
					paidByCard(target, outcome);
				}
				return result;
			}
			if (outcome.kind === 'refused' && shiftRefusal(outcome.code)) {
				// A paid order's void with no shift where the cash goes (UC-60):
				// the approval is not the problem, so Orders says what is.
				await readStoreState();
				ordersFailure = voidRefusal(language, shiftState());
				asking = null;
			}
			return result;
		},
	);
	$effect(() => () => approval.stop());

	// Pick a table (UC-39), over Sell, and why the cart asks again: the
	// server wanted a table the till did not know the shop had, or the one
	// the cart named is gone.
	let tableOpen = $state(false);
	let tableFlag = $state<'pick' | 'gone' | null>(null);

	// Pick a driver (T33, UC-4.R6), over Sell, and why the cart asks again:
	// the store wanted a driver, or the one the cart named is gone. In change
	// mode (UC-66), over Orders: the open delivery whose driver changes, the
	// key that names this opening's change, and why the last one did not go.
	let driverOpen = $state(false);
	// The sheet opened from Send: a tap on a driver also finishes the Send.
	let driverSends = false;
	let driverFlag = $state<'pick' | 'gone' | null>(null);
	let driverChanging = $state<Order | null>(null);
	let driverKey = '';
	let driverSaving = $state(false);
	let driverFailure = $state('');

	// A removed line waits ten seconds before it is really gone, so a wrong
	// tap costs a tap rather than a re-rung order. The ten seconds are the
	// deadline Notice's own (UNDO_MS). Held here because a line can be
	// removed from the cart or from its editor.
	let undo = $state<{ line: DraftLine; at: number; discount: OrderDiscount | null } | null>(null);

	// What each sheet holds while it is closed: the editor's answers, the
	// tender typed so far. The delivery's steps keep themselves
	// (src/till/delivery.svelte.ts).
	let work = $state<EditorWork | null>(null);
	// The open editor's note shortcuts (UC-3.R8): asked as the sheet opens,
	// never waited for, and kept for that item's open editor alone.
	let shortcuts = $state<{ itemId: string; words: string[] } | null>(null);
	let shortcutsAsked = 0;
	let tender = $state('');
	// The customer sheet a manager opens over the delivery details (UC-18).
	let customerOpen = $state(false);

	let saleFailure = $state('');
	let paying = $state<PayWay>(null);
	let retrying = $state(false);
	// A Send in flight, and whether it has run past 300 ms, when it says so.
	let sending = $state(false);
	let sendSlow = $state(false);
	// A discount given to the sent order is being written.
	let givingDiscount = $state(false);

	let day = $state<Day | null>(null);
	let dayLoading = $state(false);
	let dayFailed = $state(false);
	let ordersFailure = $state('');
	let voidClosedFor = $state('');
	// Which closed orders have not reached Bosla, as the store last said.
	let sends = $state<Sends | null>(null);

	// One action at a time on the Orders screen and the Done sheet: a second
	// print is a second receipt and a second settle is a second attempt at
	// money. `slow` turns the pending button's words on after 300 ms.
	let pendingAction = $state<string | null>(null);
	let slow = $state(false);
	let printing = $state(false);
	// Where the newest receipt is, for the Done screen's line, which
	// says a wait only once it has run past 300 ms.
	let printPhase = $state<PrintPhase>('idle');
	let printSlow = $state(false);
	// The receipt the store holds for a decision (#515), which Done's Print
	// another copy decides, and why that decision did not go through.
	let heldReceiptId = $state<string | null>(null);
	let receiptRefusal = $state('');
	// Why the last decision on Orders did not go through, and the job it was on.
	let decideFailure = $state<{ jobId: string; text: string } | null>(null);

	const menu = $derived(currentMenu());
	const areas = $derived(currentAreas());
	const draft = $derived(currentDraft());
	const pending = $derived(currentPending());
	// The sent order on Sell, as the till's store holds it (#348).
	const open = $derived(currentOpenOrder());
	const minorDigits = $derived(till.settings.minorDigits);

	// The fee between two taps: a named area's own fee is the best guess the
	// till has; with no area it has none, because the shop's default is not
	// sent to a till. The quote settles it either way.
	const quotedFee = $derived(currentQuotedFee());
	const areaFeeMinor = $derived(
		draft.type === 'delivery'
			? (quotedFee ?? areas.find((area) => area.id === draft.deliveryAreaId)?.feeMinor ?? 0)
			: 0,
	);
	const totals = $derived(draftTotals(areaFeeMinor, minorDigits));
	// The cart keeps the fee it shows, for the lock screen, which reads the
	// stored cart before anybody signs in and so cannot ask for it (UC-35).
	$effect(() => {
		const fee = areaFeeMinor;
		untrack(() => noteDeliveryFee(fee));
	});
	const isManager = $derived(session.role === 'manager');
	// T28 over Till settings, while it is the view and the till is not locked.
	const sharingShows = $derived(sharingOpen && isManager && view === 'settings' && !locked);
	// T32 over Till settings, as T28 is.
	const cashPointShows = $derived(cashPointOpen && isManager && view === 'settings' && !locked);
	// T34 over Till settings, as T28 is.
	const driversShows = $derived(driversOpen && isManager && view === 'settings' && !locked);
	// A manager's Till settings reads the drivers as it opens, for its
	// Drivers row's count; T34 reads them again as it opens.
	$effect(() => {
		if (view === 'settings' && isManager && !locked) untrack(() => void tillDrivers.read());
	});
	// Till settings' Sharing row says the mesh's state, so a manager's Till
	// settings reads the mesh as it opens; the sharing step's page reads it
	// itself while it shows (T28).
	const listsSharing = $derived(
		isManager && knownSteps(tillSetup.setup).some((step) => step.id === 'sharing'),
	);
	$effect(() => {
		if (view === 'settings' && listsSharing) untrack(() => void tillMesh.read());
	});
	// The onboarding (T27) shows over Sell for the manager whose sign-in read
	// it, while the store says it is pending at a step this screen knows, and
	// only once no other Sheet is open (setupShows in src/till/setupSteps.ts).
	// Its Close holds for the session that closed it; a lock hides it, and the
	// next unlock reads it again.
	const setupContext = $derived({
		printersRead: printersRead(),
		printerCount: printers().length,
		notAnswering: notAnsweringPurposes(),
		meshState: tillMesh.state,
		cashPoint: tillCashPoint.body?.cashPoint ?? null,
		cashPointFailed: tillCashPoint.failed,
		cashPointReady: tillCashPoint.ready,
	});
	// Every other Sheet that can be open over the till.
	const otherSheetOpen = $derived(
		sheet !== null ||
			discountOpen ||
			tableOpen ||
			driverOpen ||
			mineOpen ||
			displayOpen ||
			customerOpen ||
			cancelling !== null ||
			asking !== null ||
			printerSetupOpen ||
			printerIdentifyOpen ||
			openShiftOpen ||
			closeShiftOpen ||
			cashMoveOpen ||
			sharingShows ||
			cashPointShows ||
			driversShows,
	);
	const setupOpen = $derived(
		setupShows({
			isManager,
			userId: session.userId ?? '',
			readFor: setupUserId,
			closedBy: setupClosedFor,
			unread: tillSetup.loading || tillSetup.failed || tillSetup.forbidden,
			body: tillSetup.setup,
			context: setupContext,
			index: setupIndex,
			view,
			locked,
			otherSheet: otherSheetOpen,
		}),
	);
	// A standing line under the header: the last sale's refusal, or a discount
	// the owner removed, which came off the cart (UC-35).
	const notice = $derived(
		saleFailure || (discountRemoved() ? message(language, 'discountRemoved') : ''),
	);

	// The delivery details' steps, and what Confirm would give the order: the
	// sheet's title, Back, fee and actions all follow the step.
	const countryCode = $derived(till.settings.defaultCountryCode);
	const areaIds = $derived(areas.map((area) => area.id));
	const dl = $derived(deliverySteps());
	const dlContext = $derived(stepContext(areaIds, countryCode));
	const dlValues = $derived(stepValues(dl, dlContext));
	const dlOnSaved = $derived(dl.step === 'where' && !!dl.customer && !dl.addrNew);
	const dlTitle = $derived(
		message(
			language,
			dl.step === 'phone'
				? 'deliveryStepPhone'
				: dl.step === 'where'
					? 'deliveryStepWhere'
					: 'deliveryNewCustomer',
		),
	);
	// The fee for the area the step has picked. In a shop with no areas it is
	// the shop's own fee, which only the quote knows.
	const dlFee = $derived.by(() => {
		if (dl.step === 'phone') return undefined;
		if (areaIds.length === 0) return formatMinor(areaFeeMinor, minorDigits);
		const picked = dlOnSaved ? pickedAddress(dl) : undefined;
		const areaId = picked ? addressArea(picked, areaIds) || dl.pickArea : dl.form.areaId;
		const area = areas.find((z) => z.id === areaId);
		return area ? formatMinor(area.feeMinor, minorDigits) : '—';
	});

	function openDelivery() {
		openDeliverySteps(countryCode);
		sheet = { name: 'delivery' };
	}

	// Confirm: the order takes the details and the customer is saved (UC-18),
	// never awaited and never reported. Close keeps every step as it was.
	function finishDelivery() {
		if (!dlValues) return;
		confirmDelivery(dlValues, countryCode);
		closeDelivery();
	}

	function closeDelivery() {
		customerOpen = false;
		sheet = null;
	}

	// A delivery's fee is the server's to decide, so the till asks whenever
	// something that could change it changes. A takeaway never asks.
	$effect(() => {
		const signature = [
			draft.type,
			draft.deliveryAreaId,
			draft.lines.length,
			draft.customerPhone,
		].join('|');
		void signature;
		void quoteDelivery();
	});

	$effect(() => {
		attachDraft(till.tillId, session.userId ?? '');
		// A cart that names an open order goes on with it, as the store
		// holds it now. Its read of the draft this effect just wrote must
		// not become a dependency, or each write would run the effect again.
		untrack(() => void loadOpenOrder());
		void loadMenu();
		void loadAreas();
		// Its first read looks at the list in hand, which this effect must not
		// follow, or each answer would run this whole effect again.
		untrack(() => void readTables());
		// The drivers too, after each unlock (T33), as the tables.
		untrack(() => void readDrivers());
		void loadMyItems(session.userId ?? '');
	});

	// The store's state is read again at each sign-in and unlock, so whether
	// the till takes a payment (UC-60) shows at once, not at the next poll.
	$effect(() => {
		if (locked) return;
		untrack(() => void readStoreState());
	});

	// Printers list loaded on mount and refreshed every 30 s while mounted
	// and signed in; print watch polls the store's jobs with the component's lifetime.
	$effect(() => {
		if (locked) return;
		void loadPrinters();
		startPrintWatch();
		const timer = setInterval(() => {
			void loadPrinters();
		}, 30_000);
		return () => {
			clearInterval(timer);
			stopPrintWatch();
		};
	});

	// The poll that carries an owner's price change to the screen, once the
	// till's order store has read it on its own check, and takes a discount
	// the owner removed off the cart.
	$effect(() => {
		const tick = setInterval(() => {
			void loadMenu();
			// An open order the store could not read yet, as before it is
			// ready to sell, is read again until Sell shows it (#348).
			if (draft.openOrderId && !open) void loadOpenOrder();
		}, MENU_POLL_MS);
		return () => clearInterval(tick);
	});

	// A newer bundle waits (INT-7). The till moves to it only at rest
	// (src/till/rest.ts): on the sell screen with nothing open over it, the
	// cart empty, no sale waiting for an answer, no receipt printing and no
	// address removal waiting or on its way.
	const atRest = $derived(
		tillAtRest({
			view,
			// Pick a table is a sheet too, and opens on an empty cart, as Open
			// shift (T30) does from the header's Notice; Close shift (T31) opens
			// only over Shift, which is not at rest anyway. So is the onboarding
			// (#774), the sharing step over Till settings (#775) and the
			// cash-point step over it (#792).
			sheetOpen:
				sheet !== null ||
				tableOpen ||
				driverOpen ||
				openShiftOpen ||
				closeShiftOpen ||
				cashMoveOpen ||
				setupOpen ||
				sharingShows ||
				cashPointShows ||
				driversShows,
			mineOpen,
			displayOpen,
			undoRunning: undo !== null || tillDrivers.unsettled,
			paying: paying !== null,
			retrying,
			pendingSale: pending !== null,
			printing,
			// A decision on its way holds the till as a print does; a held
			// print alone does not (#515).
			deciding: decisionsPending(),
			addressRemoval: addressRemovalUnsettled(),
			openOrder: draft.openOrderId !== '',
			draft: {
				lines: draft.lines.length,
				customerPhone: draft.customerPhone,
				customerName: draft.customerName,
				deliveryAddress: draft.deliveryAddress,
				deliveryLandmark: draft.deliveryLandmark,
				note: draft.note,
			},
		}),
	);
	// Whether nobody is at the till's own lock: its own cashier still picked,
	// nothing typed or shown. What the till asks the shell is decided with the
	// lock, below (restAsk).
	const ownLockIdle = $derived(
		ownLockAtRest({
			digits: entry.digits,
			phase: entry.phase,
			pickedId: entry.picked?.id ?? null,
			signedInId: session.userId ?? '',
		}),
	);

	// ---------- the editor ----------

	// The sheet opens at once; the shortcuts join it when the store answers,
	// unless the editor closed or moved to another item before then.
	function askShortcuts(itemId: string) {
		const asked = ++shortcutsAsked;
		shortcuts = null;
		void noteShortcuts(itemId).then((words) => {
			if (asked !== shortcutsAsked || sheet?.name !== 'editor' || work?.itemId !== itemId) return;
			shortcuts = { itemId, words };
		});
	}

	function ask(item: Item) {
		// Tapping the same item again comes back to the answers left open.
		// An item sold by weight starts a portion, its grams asked first (#917).
		if (!(work && work.key === '' && work.itemId === item.id)) {
			work = soldByWeight(item)
				? workForWeighed(item.id, item.name, item.priceMinor)
				: workForNew(item.id, item.name, item.priceMinor);
		}
		sheet = { name: 'editor' };
		askShortcuts(item.id);
	}

	function editLine(line: DraftLine) {
		if (!(work && work.key === line.key)) work = workFromLine(line);
		sheet = { name: 'editor' };
		askShortcuts(line.itemId);
	}

	function finishEdit() {
		if (!work || blocked(menu, work)) return;
		const modifiers = pickedModifiers(menu, work.chosen);
		// A weighed portion keeps its grams and kilo price, and the base they
		// cost as its unitBaseMinor, so an older bundle prices it alike (#917).
		const weight =
			work.perKgMinor !== undefined
				? { grams: work.grams ?? 0, perKgMinor: work.perKgMinor }
				: undefined;
		const unitBaseMinor = baseOf(work);
		if (work.key === '') {
			addLine({
				itemId: work.itemId,
				name: work.name,
				unitBaseMinor,
				modifiers,
				qty: weight ? 1 : work.qty,
				note: work.note,
				...(weight ? { weight } : {}),
			});
		} else {
			updateLine(work.key, {
				modifiers,
				qty: weight ? 1 : work.qty,
				note: work.note,
				weight,
				unitBaseMinor,
			});
		}
		work = null;
		sheet = null;
	}

	function removeFromCart(line: DraftLine) {
		const at = draft.lines.findIndex((l) => l.key === line.key);
		if (at < 0) return;
		// The last line takes the discount with it; its Undo puts both back, except a
		// list discount the last-read list no longer offers (undoLineRemoval).
		const discount = removeLine(line.key);
		if (work?.key === line.key) work = null;
		undo = { line, at, discount };
		// An empty cart is a sale that is over, and so is the notice that its
		// discount was removed.
		if (draft.lines.length === 0) forgetRemoved();
	}

	function removeEdited() {
		const line = draft.lines.find((l) => l.key === work?.key);
		sheet = null;
		if (line) removeFromCart(line);
	}

	// The line comes back with its discount (UC-35): a typed one always, a list
	// one while the last-read list offers it; one removed meanwhile stays off,
	// and the cart says why.
	function undoRemove() {
		if (!undo) return;
		undoLineRemoval(undo.line, undo.at, undo.discount);
		undo = null;
	}

	// ---------- paying ----------

	const payTotal = $derived(
		sheet?.name === 'pay' && sheet.collect ? sheet.collect.totalMinor : (totals?.totalMinor ?? 0),
	);
	const typedMinor = $derived(parseTypedMinor(tender));
	// An order of 0 takes no tender: Exact pays it with 0 and Card with none,
	// and the server refuses any other cash (UC-35).
	const tenderReady = $derived(
		payTotal > 0 &&
			typedMinor !== null &&
			change('cash', payTotal, typedMinor).ok &&
			paying === null,
	);

	// Pay takes the payment of the sent order on Sell (#348): every order is
	// sent before it is paid, so there is always one to collect.
	function openPay() {
		if (!open) return;
		saleFailure = '';
		forgetRemoved();
		takePayment(open);
	}

	function bill() {
		const order = currentOpenOrder();
		if (!order || draft.lines.length > 0) return;
		saleFailure = '';
		void run(`bill:${order.id}`, async () => {
			const job = await queueBill(order.id);
			if (!job) {
				// The store queued nothing, so no print notice will name it.
				saleFailure = message(language, 'sellBillFailed');
			} else if (!(await waitForBill(order.id, job.id))) {
				// A failed or held job is said by its print notice under the header.
				await refreshPrintJobs();
			} else {
				await loadOpenOrder();
				await refreshDay();
			}
		});
	}

	// Print again on a bill's notice records the reprint, so the notice goes.
	// The order reloads once the bill prints, so a "changed since printed"
	// mark of the order on Sell clears.
	function printNoticeAgainHere(pNotice: PrintNotice) {
		void printNoticeAgain(
			pNotice,
			pNotice.document === 'bill'
				? async () => {
						await loadOpenOrder();
						await refreshDay();
					}
				: undefined,
		);
	}

	// Send puts the cart's lines not sent yet on the order (#348): the first
	// Send places it open, a later one adds to it. The order stays on Sell.
	async function send() {
		if (sending || paying) return;
		sending = true;
		sendSlow = false;
		saleFailure = '';
		const timer = setTimeout(() => (sendSlow = true), 300);
		try {
			if (draft.openOrderId && !open) {
				// The order these lines go on has not shown on Sell yet: the
				// till reads it first, so the cashier sees it before a line is
				// added to it, and taps Send again. One no longer open leaves
				// the cart, whose lines are then a new order.
				await loadOpenOrder();
				if (draft.openOrderId) {
					if (!currentOpenOrder()) saleFailure = message(language, 'tillNotReady');
					return;
				}
			}
			if (draft.openOrderId) settleAddition(await addToOrder());
			else {
				// While the drivers' first read is on its way, the till waits
				// for it: a server older than drivers then sends as before.
				if (driverNeeded() && driversRead() !== 'ok') await readDrivers();
				// A delivery's first Send asks which driver (T33): one tap on
				// him finishes it.
				if (driverNeeded()) openDrivers();
				else settle(await checkout(null, totals?.totalMinor ?? 0), 'cash');
			}
		} finally {
			clearTimeout(timer);
			sending = false;
			sendSlow = false;
		}
	}

	function settleAddition(outcome: AddOutcome) {
		if (outcome.kind === 'added') return;
		if (outcome.kind === 'unreachable') saleFailure = message(language, 'offline');
		else if (outcome.kind === 'notReady') saleFailure = message(language, 'tillNotReady');
		else if (outcome.kind === 'unconfirmed') saleFailure = message(language, 'sellPendingTitle');
		else if (outcome.code === 'ALREADY_PAID') saleFailure = message(language, 'ordersPaidAlready');
		else if (notReadyCode(outcome.code)) saleFailure = message(language, 'tillNotReady');
		else saleFailure = message(language, 'payFailedKept');
	}

	// New order empties Sell for the next one and leaves the sent order open;
	// Orders opens it again.
	function newOrder() {
		if (draft.lines.length > 0 || sending) return;
		clearDraft();
		showOpenOrder(null);
		saleFailure = '';
		driverFlag = null;
		forgetRemoved();
	}

	// Open on Orders: go on with an open order on Sell, unless Sell holds lines
	// not sent yet, which would be lost.
	function openOnSell(order: Order) {
		if (!goOnWith(order.id, order.type as 'dine_in' | 'takeaway' | 'delivery')) return;
		showOpenOrder(order);
		saleFailure = '';
		ordersFailure = '';
		view = 'sell';
	}

	// ---------- the table (UC-39) ----------

	// Said while the dine-in still has no table in a shop that has one: a shop
	// whose last table went says nothing, and sells dine-in with none.
	const tableNotice = $derived(
		draft.type === 'dine_in' && tableFlag && tableNeeded()
			? message(language, tableFlag === 'gone' ? 'tableRemoved' : 'tableNeeded')
			: '',
	);

	// The list is read again each time the sheet opens; the cart's table
	// coming off because the owner removed it is said on Sell.
	async function readTables() {
		if (await loadTables()) tableFlag = 'gone';
		// A shop with no table to offer has nothing to pick from.
		if (!shopHasTables()) tableOpen = false;
	}

	function openTables() {
		tableOpen = true;
		void readTables();
	}

	// Dine in asks which table in a shop that has tables; in one with none it
	// only reads the list again, which an owner may have just filled.
	function dineIn() {
		if (shopHasTables()) openTables();
		else void readTables();
	}

	// One tap on a table picks it and closes the sheet.
	function pickTable(table: Table) {
		setTable(table);
		tableFlag = null;
		saleFailure = '';
		tableOpen = false;
	}

	// ---------- the driver (UC-4.R6, UC-66) ----------

	// Said while the delivery still has no driver after the store asked for
	// one, or after the one it named went.
	const driverNotice = $derived(
		draft.type === 'delivery' && driverFlag && driverNeeded()
			? message(language, driverFlag === 'gone' ? 'driverRemoved' : 'driverNeeded')
			: '',
	);

	// The list is read again each time the sheet opens; the cart's driver
	// coming off because he was removed is said on Sell.
	async function readDrivers() {
		if (await loadDrivers()) driverFlag = 'gone';
	}

	// A delivery's first Send with no driver opens the sheet, with none
	// picked, and a tap on a driver finishes the Send; a tap on the cart's
	// driver opens it to pick another, and sends nothing.
	function openDrivers(sends = true, read = true) {
		driverSends = sends;
		driverChanging = null;
		driverFailure = '';
		driverOpen = true;
		if (read) void readDrivers();
	}

	// Close leaves the open delivery as it was. It waits while a change is on
	// its way, unless a lock closes it: the next person signed in never sees
	// the last one's change, and opens it afresh under a new key.
	function closeDrivers(force = false) {
		if (driverSaving && !force) return;
		driverOpen = false;
		driverChanging = null;
		driverFailure = '';
	}

	// One tap on a driver: on Sell it picks him and finishes the Send; in
	// change mode it changes the open delivery's driver (UC-66).
	function pickDriver(driver: Driver) {
		if (driverChanging) {
			changeTo(driverChanging, driver);
			return;
		}
		setDriver(driver);
		driverFlag = null;
		saleFailure = '';
		driverOpen = false;
		if (driverSends) void send();
	}

	// Change driver on an open delivery of Orders (UC-66): the sheet in change
	// mode, its driver marked, under one key for this opening, so a tap again
	// after a lost answer is answered as before.
	function openDriverChange(order: Order) {
		ordersFailure = '';
		driverFailure = '';
		driverKey = newWriteKey();
		driverChanging = order;
		driverOpen = true;
		void readDrivers();
	}

	// A refusal of a driver's change in the words the sheet says it in: a
	// NOT_FOUND names the driver only at driverId; one that names no field is
	// the order, which is not on this till's store.
	function driverChangeFailure(code: string, path?: unknown): string {
		if (driverClosedCode(code)) return message(language, 'driverClosed');
		if (code === 'DRIVER_INACTIVE' || (code === 'NOT_FOUND' && path === 'driverId'))
			return message(language, 'driverRemoved');
		if (notReadyCode(code)) return message(language, 'tillNotReady');
		return message(language, 'ordersFailed');
	}

	function driverClosedCode(code: string): boolean {
		return code === 'ALREADY_PAID' || code === 'ALREADY_VOID';
	}

	function changeTo(order: Order, driver: Driver) {
		if (driverSaving) return;
		driverFailure = '';
		// The order's own driver again is no change, and nothing is sent.
		if (driver.id === order.driverId) return;
		const op: ApprovedOperation = { kind: 'driver', orderId: order.id, driverId: driver.id };
		const key = `${driverKey}:${driver.id}`;
		if (!isManager) {
			askManager(op, fill(message(language, 'driverChangeTitle'), orderNumber(order)), key);
			return;
		}
		void (async () => {
			driverSaving = true;
			try {
				const outcome = await runApproved(op, key, null);
				// A lock closed the sheet meanwhile: Orders reads the order again.
				if (driverChanging?.id !== order.id) {
					void refreshDay();
					return;
				}
				if (outcome.kind === 'done') {
					driverSaving = false;
					closeDrivers();
					void refreshDay();
					return;
				}
				driverFailure =
					outcome.kind === 'unreachable'
						? message(language, 'offline')
						: driverChangeFailure(outcome.code, outcome.details?.path);
				if (outcome.kind === 'refused' && driverClosedCode(outcome.code)) void refreshDay();
			} finally {
				driverSaving = false;
			}
		})();
	}

	// One tap on a discount gives it and closes the sheet; None takes it off.
	// On a sent order it is written at once, with no Send (#348), and the
	// sheet closes once the order has it.
	async function giveDiscount(discount: OrderDiscount | null) {
		saleFailure = '';
		forgetRemoved();
		if (!draft.openOrderId) {
			setDiscount(discount);
			discountOpen = false;
			return;
		}
		if (givingDiscount) return;
		givingDiscount = true;
		try {
			const outcome = await changeOrderDiscount(discount);
			discountOpen = false;
			if (outcome.kind === 'refused') {
				saleFailure = message(language, discountRefusalMessage(outcome.code));
			} else if (outcome.kind === 'unreachable') {
				saleFailure = message(language, 'ordersFailed');
			}
		} finally {
			givingDiscount = false;
		}
	}

	// The Done screen's line for an order its discount made free: the discount
	// and what it took.
	function freeOf(order: PaidOrder): Settled['free'] {
		if (order.status !== 'paid' || order.totalMinor !== 0 || !order.discount) return null;
		return { discount: discountTaken(language, order.discount, order.discountMinor, minorDigits) };
	}

	function confirmTender() {
		if (!tenderReady || typedMinor === null) return;
		settleCash(typedMinor, 'confirm');
	}

	// Every payment is of a sent order (#348): the sheet always collects one.
	function settleCash(tenderedMinor: number, way: PayWay) {
		if (sheet?.name !== 'pay' || !sheet.collect) return;
		void collect({ method: 'cash', tenderedMinor }, way);
	}

	// A card payment of a driver's delivery needs a manager (UC-16.R9): a
	// manager signed in pays it at once; anyone else asks one on Manager
	// approval (T20) over Payment, under the payment's own key.
	function settleCard() {
		if (sheet?.name !== 'pay' || !sheet.collect) return;
		const target = sheet.collect;
		if (!target.cardNeedsManager) {
			void collect({ method: 'card', tenderedMinor: 0 }, 'card');
			return;
		}
		const op: ApprovedOperation = { kind: 'pay', orderId: target.orderId };
		const key = settleKey(target.orderId, 'pay');
		if (!isManager) {
			saleFailure = '';
			askManager(op, fill(message(language, 'cardApprovalTitle'), target.number), key);
			return;
		}
		if (paying) return;
		paying = 'card';
		saleFailure = '';
		void (async () => {
			try {
				paidByCard(target, await runApproved(op, key, null));
			} finally {
				paying = null;
			}
		})();
	}

	// What a card payment a manager approved answered: Done for a paid order,
	// else Payment says why, as a payment's own refusal does.
	function paidByCard(target: Collect, outcome: ApprovedOutcome) {
		if (outcome.kind === 'done' && outcome.placed) {
			const placed = outcome.placed;
			void readStoreState();
			tender = '';
			clearDraft();
			showOpenOrder(null);
			newDoneScreen();
			sheet = {
				name: 'done',
				settled: {
					method: 'card',
					changeMinor: placed.changeMinor,
					totalMinor: placed.order.totalMinor,
					number: orderNumber(placed.order),
					orderId: target.orderId,
					drawerJobId: placed.drawerJobId,
					free: freeOf(placed.order),
				},
			};
			return;
		}
		if (outcome.kind === 'refused' && outcome.code === 'ALREADY_PAID') {
			saleFailure = message(language, 'ordersPaidAlready');
			sheet = null;
			return;
		}
		if (outcome.kind === 'refused' && shiftRefusal(outcome.code)) {
			void readStoreState();
			saleFailure = '';
			return;
		}
		saleFailure =
			outcome.kind === 'refused'
				? message(language, notReadyCode(outcome.code) ? 'tillNotReady' : 'ordersFailed')
				: message(language, 'offline');
	}

	async function retryPending() {
		if (retrying) return;
		retrying = true;
		try {
			if (additionPending()) {
				settleAddition(await addToOrder());
				return;
			}
			const outcome = await resumeCheckout();
			if (outcome === null) {
				endCheckout();
				return;
			}
			settle(outcome, 'cash');
		} finally {
			retrying = false;
		}
	}

	function settle(outcome: CheckoutOutcome, method: Settled['method']) {
		if (outcome.kind === 'alreadySent') return;
		if (outcome.kind === 'placed' && outcome.placed.order.status === 'open') {
			// Sent, not paid: the order stays on Sell, with its number.
			sheet = null;
			return;
		}
		if (outcome.kind === 'placed') {
			// The store's state after each write of the screen's (N14).
			void readStoreState();
			const order = outcome.placed.order;
			tender = '';
			newDoneScreen();
			sheet = {
				name: 'done',
				settled: {
					method: (order.payment?.method as 'cash' | 'card' | undefined) ?? method,
					changeMinor: outcome.placed.changeMinor,
					totalMinor: order.totalMinor,
					number: orderNumber(order),
					orderId: order.id,
					drawerJobId: outcome.placed.drawerJobId,
					free: freeOf(order),
				},
			};
			return;
		}
		if (outcome.kind === 'alreadyPlaced') {
			// Not a failure. The order exists; show its number and move on. The
			// key's answer names the till's number; its prefix is the till's.
			newDoneScreen();
			sheet = {
				name: 'done',
				settled: {
					method: 'cash',
					changeMinor: 0,
					totalMinor: 0,
					number: tillOrderNumber(till.prefix, outcome.number),
					orderId: '',
				},
			};
			return;
		}
		if (outcome.kind === 'priceChanged') {
			saleFailure = message(language, 'sellPriceChanged');
			sheet = null;
			return;
		}
		// Back to Sell for every discount refusal (UC-35): the cart says what
		// to change, and nothing was sold.
		if (outcome.kind === 'discountRemoved') {
			// Already off the cart; the standing line says why.
			saleFailure = '';
			sheet = null;
			return;
		}
		if (outcome.kind === 'discountManagerOnly') {
			saleFailure = message(language, 'discountManagerOnly');
			sheet = null;
			return;
		}
		if (outcome.kind === 'discountsDown') {
			saleFailure = message(language, 'discountsDown');
			sheet = null;
			return;
		}
		if (outcome.kind === 'discountRefused') {
			saleFailure = message(language, 'discountRefused');
			sheet = null;
			return;
		}
		// Back to Sell for a table the server refused (UC-39): the cart says
		// why, and Pick a table opens on the list read again.
		if (outcome.kind === 'tableNeeded' || outcome.kind === 'tableRemoved') {
			saleFailure = '';
			tableFlag = outcome.kind === 'tableNeeded' ? 'pick' : 'gone';
			sheet = null;
			openTables();
			return;
		}
		// Back to Sell for a driver the store refused (UC-4.R6): the cart says
		// why, and Pick a driver opens on the list read again.
		if (outcome.kind === 'driverNeeded' || outcome.kind === 'driverRemoved') {
			saleFailure = '';
			driverFlag = outcome.kind === 'driverNeeded' ? 'pick' : 'gone';
			sheet = null;
			// The refusal already read the list again.
			openDrivers(true, false);
			return;
		}
		if (outcome.kind === 'unreachable') {
			saleFailure = message(language, 'offline');
			return;
		}
		if (outcome.kind === 'notReady') {
			// Nothing was sold; the Payment sheet stays, and so does the cart.
			saleFailure = message(language, 'tillNotReady');
			return;
		}
		if (outcome.kind === 'unconfirmed') {
			// The sale stays unsent, and the cart says so beside its Retry.
			saleFailure = message(language, 'sellPendingTitle');
			return;
		}
		saleFailure = message(language, 'payFailedKept');
	}

	function nextSale() {
		drawerWait?.abort();
		sheet = null;
		saleFailure = '';
		tableFlag = null;
		driverFlag = null;
		forgetRemoved();
	}

	// ---------- printing ----------

	let drawerPhase = $state<'idle' | 'watching' | 'failed' | 'uncertain'>('idle');
	let drawerJobId: string | undefined;
	let drawerWait: AbortController | null = null;
	// A held kick (#515): Open drawer now is offered while the store lists it
	// among the kick's choices (its 2 minutes, by the store's clock, and no
	// retry yet), and why its decision did not go through.
	let drawerRefusal = $state('');
	const drawerNow = $derived(
		drawerPhase === 'uncertain' &&
			heldJobs().some(
				(job) => job.id === drawerJobId && heldChoices(job).includes('open_drawer_now'),
			),
	);

	$effect(() => {
		const jobId = sheet?.name === 'done' ? sheet.settled.drawerJobId : undefined;
		untrack(() => {
			drawerJobId = jobId;
			drawerPhase = 'idle';
			drawerRefusal = '';
			if (jobId) void watchKick(jobId);
		});
		return () => {
			drawerWait?.abort();
			drawerWait = null;
		};
	});

	async function watchKick(jobId: string) {
		drawerWait?.abort();
		const controller = new AbortController();
		drawerWait = controller;
		drawerPhase = 'watching';
		const result = await watchDrawer(jobId, controller.signal);
		if (controller.signal.aborted) return;
		if (typeof result === 'object') {
			// Held for a decision: Retry becomes Open drawer now, while the store
			// offers it.
			drawerPhase = 'uncertain';
			await refreshPrintJobs();
		} else {
			drawerPhase = result === 'failed' ? 'failed' : 'idle';
		}
	}

	// Open drawer now (#515): the decision makes one new kick, a retry of the
	// held one, which Done watches as Retry watches its retry.
	async function openNow() {
		if (!drawerJobId || !drawerNow) return;
		const controller = drawerWait;
		const heldId = drawerJobId;
		drawerPhase = 'watching';
		drawerRefusal = '';
		const outcome = await openDrawerNow(heldId, decideHeld);
		if (controller?.signal.aborted || sheet?.name !== 'done') return;
		if (outcome.kind === 'opened') {
			drawerJobId = outcome.jobId;
			void watchKick(outcome.jobId);
			return;
		}
		if (outcome.kind === 'refused' && outcome.code === 'PRINT_JOB_NOT_UNCERTAIN') {
			drawerPhase = 'idle';
			drawerRefusal = message(language, 'printDecidedAlready');
			return;
		}
		drawerPhase = 'uncertain';
		if (outcome.kind === 'refused' && outcome.code === 'VALIDATION_ERROR') await refreshPrintJobs();
		drawerRefusal = decisionRefusal(outcome.kind === 'unreachable' ? null : outcome, 'drawer');
	}

	// A decision's refusal in cashier words (#515).
	function decisionRefusal(outcome: DecideOutcome | null, doc: PrintJob['document']): string {
		if (outcome === null) return message(language, 'offline');
		if (outcome.kind === 'decided') return '';
		if (outcome.code === 'PRINT_JOB_NOT_UNCERTAIN') return message(language, 'printDecidedAlready');
		if (outcome.code === 'VALIDATION_ERROR' && doc === 'bill') {
			return message(language, 'printBillChangedCopy');
		}
		if (outcome.code === 'VALIDATION_ERROR' && doc === 'drawer') {
			return message(language, 'printDrawerTooLate');
		}
		if (notReadyCode(outcome.code)) return message(language, 'tillNotReady');
		return message(language, 'ordersFailed');
	}

	async function retryKick() {
		if (!drawerJobId || drawerPhase !== 'failed') return;
		const controller = drawerWait;
		drawerPhase = 'watching';
		const jobId = await retryDrawer(drawerJobId);
		if (controller?.signal.aborted || sheet?.name !== 'done') return;
		if (!jobId) {
			drawerPhase = 'failed';
			return;
		}
		drawerJobId = jobId;
		void watchKick(jobId);
	}

	// Payment queues its own receipt. Done only watches it, and closing the
	// sheet stops the watcher without changing the store's print queue.
	let printSeq = 0;
	let receiptWait: AbortController | null = null;
	let printBusy = false;

	$effect(() => {
		if (sheet?.name !== 'done' || !sheet.settled.orderId) {
			return;
		}
		const orderId = sheet.settled.orderId;
		untrack(() => void watchReceipt(orderId));
		return () => untrack(newDoneScreen);
	});

	async function watchReceipt(orderId: string, jobId?: string, seq = ++printSeq, hold = false) {
		receiptWait?.abort();
		const controller = new AbortController();
		receiptWait = controller;
		printing = true;
		printSlow = false;
		printPhase = 'printing';
		const timer = setTimeout(() => {
			if (seq === printSeq) printSlow = true;
		}, 300);
		try {
			const result = await waitForReceipt(orderId, controller.signal, jobId);
			if (seq === printSeq) {
				if (typeof result === 'object') {
					// Held for a decision: Print again becomes Print another copy.
					heldReceiptId = result.jobId;
					printPhase = 'uncertain';
				} else {
					printPhase = result === 'failed' ? 'failed' : 'idle';
				}
			}
		} finally {
			clearTimeout(timer);
			if (seq === printSeq) {
				receiptWait = null;
				printing = hold;
				printSlow = false;
			}
		}
	}

	async function print(orderId: string | null): Promise<boolean> {
		if (printing || printBusy) return false;
		printBusy = true;
		const seq = ++printSeq;
		printing = true;
		receiptRefusal = '';
		printPhase = 'printing';
		const timer = setTimeout(() => {
			if (seq === printSeq) printSlow = true;
		}, 300);
		try {
			const job = await queuePrint(orderId);
			if (seq === printSeq) {
				printPhase = !job || job.state === 'failed' ? 'failed' : 'idle';
				if (
					job &&
					job.state !== 'failed' &&
					job.state !== 'printed' &&
					orderId &&
					sheet?.name === 'done' &&
					sheet.settled.orderId === orderId
				) {
					await watchReceipt(orderId, job.id, seq, true);
				}
			}
			return job !== null && job.state !== 'failed';
		} finally {
			clearTimeout(timer);
			// Keep the button disabled for the same cooldown as before.
			if (seq === printSeq) printing = true;
			await wait(PRINT_COOLDOWN_MS);
			printBusy = false;
			if (seq === printSeq) {
				printing = false;
				printSlow = false;
			}
		}
	}

	// Print another copy (#515): the held receipt's decision prints one copy,
	// marked REPRINT and with no kick, which Done then watches as a reprint.
	async function anotherCopy() {
		if (printing || printBusy || !heldReceiptId) return;
		if (sheet?.name !== 'done' || !sheet.settled.orderId) return;
		const orderId = sheet.settled.orderId;
		const heldId = heldReceiptId;
		printBusy = true;
		const seq = ++printSeq;
		printing = true;
		receiptRefusal = '';
		printPhase = 'printing';
		const timer = setTimeout(() => {
			if (seq === printSeq) printSlow = true;
		}, 300);
		try {
			const outcome = await decideHeld(heldId, 'print_another_copy');
			if (seq !== printSeq) return;
			if (outcome?.kind === 'decided') {
				heldReceiptId = null;
				const copyId = outcome.job.decision?.replacementJobId;
				if (copyId) await watchReceipt(orderId, copyId, seq, true);
				else printPhase = 'idle';
			} else if (outcome?.kind === 'refused' && outcome.code === 'PRINT_JOB_NOT_UNCERTAIN') {
				heldReceiptId = null;
				printPhase = 'idle';
				receiptRefusal = message(language, 'printDecidedAlready');
			} else {
				printPhase = 'uncertain';
				receiptRefusal = decisionRefusal(outcome, 'receipt');
			}
		} finally {
			clearTimeout(timer);
			// Keep the button disabled for the same cooldown as Print again.
			if (seq === printSeq) printing = true;
			await wait(PRINT_COOLDOWN_MS);
			printBusy = false;
			if (seq === printSeq) {
				printing = false;
				printSlow = false;
			}
		}
	}

	function newDoneScreen() {
		printSeq++;
		receiptWait?.abort();
		receiptWait = null;
		printing = false;
		printSlow = false;
		printPhase = 'idle';
		heldReceiptId = null;
		receiptRefusal = '';
	}

	function wait(ms: number): Promise<void> {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}

	const askedForNotice = new SvelteSet<string>();

	$effect(() => {
		if (locked || view !== 'printers') return;
		untrack(() => {
			if (!printersRead()) void loadPrinters();
			// Whether the till uses the shop's list or its own setup, read each time the panel opens.
			void loadMesh();
		});
	});

	// The order a print is of, as the till last read it: the one on Sell, or the day's.
	function orderOf(orderId: string | undefined): Order | null {
		if (!orderId) return null;
		if (open && open.id === orderId) return open;
		return day?.orders?.find((o) => o.id === orderId) ?? null;
	}

	function noticeOrderNumber(pNotice: PrintNotice): string {
		const found = orderOf(pNotice.orderId);
		return found ? orderNumber(found) : '';
	}

	// A notice names its order by number. A failed job knows the order's id only, so
	// when the day does not hold that order the till reads the day once for it.
	$effect(() => {
		for (const pNotice of printNotices()) {
			if (!pNotice.orderId || noticeOrderNumber(pNotice) !== '') continue;
			if (askedForNotice.has(pNotice.orderId)) continue;
			askedForNotice.add(pNotice.orderId);
			void refreshDay();
		}
	});

	function noticeText(pNotice: PrintNotice): string {
		const num = noticeOrderNumber(pNotice);
		const kitchen = pNotice.document === 'kitchen_slip';
		if (pNotice.kind === 'held') {
			if (num) return fill(message(language, kitchen ? 'printHeldKitchen' : 'printHeldBill'), num);
			return message(language, kitchen ? 'printHeldKitchenAnon' : 'printHeldBillAnon');
		}
		if (num) {
			const key = pNotice.document === 'kitchen_slip' ? 'printNoticeKitchen' : 'printNoticeBill';
			return fill(message(language, key), num);
		}
		const anonKey =
			pNotice.document === 'kitchen_slip' ? 'printNoticeKitchenAnon' : 'printNoticeBillAnon';
		return message(language, anonKey);
	}

	// The order on Sell has a bill the store holds for a decision (#515).
	const billHeld = $derived(
		open !== null && heldJobs().some((job) => job.document === 'bill' && job.orderId === open.id),
	);

	// Orders' "Waiting for a decision" (#515): every held job of this till,
	// with its order's number and the choices it offers now.
	const heldRows = $derived(
		heldJobs().map((job) => {
			const order = orderOf(job.orderId);
			return {
				job,
				number: order ? orderNumber(order) : '',
				choices: heldChoices(job),
			};
		}),
	);

	// One tap on a held job's choice on Orders sends that decision.
	function decide(job: PrintJob, decision: Decision) {
		decideFailure = null;
		ordersFailure = '';
		void run(`decide:${job.id}:${decision}`, async () => {
			const outcome = await decideHeld(job.id, decision);
			if (outcome?.kind !== 'decided') {
				decideFailure = { jobId: job.id, text: decisionRefusal(outcome, job.document) };
			}
		});
	}

	// ---------- orders ----------

	async function refreshDay() {
		dayLoading = true;
		const [next, sent] = await Promise.all([loadDay(), loadSends()]);
		dayLoading = false;
		dayFailed = next === null;
		if (next) day = next;
		if (sent) sends = sent;
	}

	// While Orders is open the till asks its store again every few seconds, so
	// a sale that reaches Bosla loses its mark on its own (#371, drill 2).
	$effect(() => {
		if (view !== 'orders') return;
		const tick = setInterval(async () => {
			const sent = await loadSends();
			if (sent) sends = sent;
		}, SENDS_POLL_MS);
		return () => clearInterval(tick);
	});

	// The top bar's count of this till's open orders (#352): read when the till
	// starts or unlocks, and again after each write the store takes.
	$effect(() => watchOpenCount());
	$effect(() => {
		if (!locked) void refreshOpenCount();
	});

	// The filter Orders opens on: the count opens it on the open orders. Every
	// tap opens Orders afresh, so it takes the filter even when Orders is
	// already on screen under the header.
	let ordersStart = $state<'all' | 'open'>('all');
	let ordersTap = $state(0);

	async function showOrders(start: 'all' | 'open' = 'all') {
		ordersStart = start;
		ordersTap++;
		view = 'orders';
		ordersFailure = '';
		voidClosedFor = '';
		decideFailure = null;
		await refreshDay();
	}

	async function run(key: string, task: () => Promise<void>) {
		if (pendingAction) return;
		pendingAction = key;
		slow = false;
		const timer = setTimeout(() => (slow = true), 300);
		try {
			await task();
		} finally {
			clearTimeout(timer);
			pendingAction = null;
			slow = false;
		}
	}

	function settleKey(orderId: string, what: string) {
		return `${what}-${orderId}-${draft.idempotencyKey}`;
	}

	function takePayment(order: Order) {
		ordersFailure = '';
		tender = '';
		saleFailure = '';
		sheet = {
			name: 'pay',
			collect: {
				orderId: order.id,
				number: orderNumber(order),
				totalMinor: order.totalMinor,
				discounted: order.discountMinor > 0,
				cardNeedsManager: cardNeedsManager(order),
			},
		};
	}

	async function collect(payment: { method: 'cash' | 'card'; tenderedMinor: number }, way: PayWay) {
		if (sheet?.name !== 'pay' || !sheet.collect || paying) return;
		const target = sheet.collect;
		paying = way;
		saleFailure = '';
		try {
			const outcome = await payOrder(target.orderId, payment, settleKey(target.orderId, 'pay'));
			// The store's state after the write; a refusal for want of a shift
			// waits for it, so Payment's Notice says why at once.
			const stateRead = readStoreState();
			if (outcome.kind === 'settled') {
				tender = '';
				// The order is paid and closed: Sell starts the next one.
				clearDraft();
				showOpenOrder(null);
				newDoneScreen();
				sheet = {
					name: 'done',
					settled: {
						method: payment.method,
						changeMinor: outcome.result.changeMinor,
						totalMinor: outcome.result.order.totalMinor,
						number: orderNumber(outcome.result.order),
						orderId: target.orderId,
						drawerJobId: outcome.result.drawerJobId,
						free: freeOf(outcome.result.order),
					},
				};
				return;
			}
			if (outcome.kind === 'refused' && shiftRefusal(outcome.code)) {
				// Nothing was paid; Payment stays open, and its Notice says why.
				await stateRead;
				saleFailure = '';
				return;
			}
			if (outcome.kind === 'refused' && outcome.code === 'ALREADY_PAID') {
				saleFailure = message(language, 'ordersPaidAlready');
				sheet = null;
				await loadOpenOrder();
				return;
			}
			if (outcome.kind === 'refused' && outcome.approval && payment.method === 'card') {
				// The store wants a manager for this card payment (UC-16.R9),
				// as the order now reads: Manager approval asks one.
				sheet.collect = { ...target, cardNeedsManager: true };
				askManager(
					{ kind: 'pay', orderId: target.orderId },
					fill(message(language, 'cardApprovalTitle'), target.number),
					settleKey(target.orderId, 'pay'),
				);
				return;
			}
			saleFailure =
				outcome.kind === 'refused'
					? message(language, notReadyCode(outcome.code) ? 'tillNotReady' : 'ordersFailed')
					: message(language, 'offline');
		} finally {
			paying = null;
		}
	}

	// Only a manager voids (#379): a manager signed in voids at once, and
	// anyone else asks one on Manager approval.
	function void_(order: Order, reasonKey: ReasonKey, reason: string) {
		ordersFailure = '';
		voidClosedFor = '';
		const op: ApprovedOperation = { kind: 'void', orderId: order.id, reasonKey, reason };
		const key = settleKey(order.id, 'approved-void');
		if (!isManager) {
			askManager(op, fill(message(language, 'voidTitle'), orderNumber(order)), key);
			return;
		}
		void run(`void:${order.id}:${reason}`, async () => {
			const outcome = await runApproved(op, key, null);
			const stateRead = readStoreState();
			if (outcome.kind !== 'done') {
				const code = outcome.kind === 'refused' ? outcome.code : '';
				if (code === 'VOID_WINDOW_CLOSED') voidClosedFor = order.id;
				else if (shiftRefusal(code)) {
					await stateRead;
					ordersFailure = voidRefusal(language, shiftState());
				} else if (code === 'ALREADY_PAID') ordersFailure = message(language, 'ordersPaidAlready');
				else if (outcome.kind === 'unreachable') ordersFailure = message(language, 'offline');
				else if (notReadyCode(code)) ordersFailure = message(language, 'tillNotReady');
				else ordersFailure = message(language, 'ordersFailed');
			}
			await refreshDay();
		});
	}

	// ---------- a sent line's cancellation, and a manager's approval ----------

	function openCancel(line: SentLine) {
		cancelFailure = '';
		cancelSaving = null;
		cancelKeyBase = newWriteKey();
		cancelOpened++;
		cancelling = line;
	}

	function cancelReason(qty: number, reasonKey: ReasonKey, reason: string) {
		const order = open;
		const line = cancelling;
		if (!order || !line?.lineId || cancelSaving !== null) return;
		cancelFailure = '';
		const op: ApprovedOperation = {
			kind: 'cancel',
			orderId: order.id,
			lineId: line.lineId,
			qty,
			reasonKey,
			reason,
		};
		// One key for each choice made on this opening of the sheet: a tap
		// again after a lost answer sends the same cancellation under the same
		// key, which the store answers as before rather than cancelling twice.
		const key = `${cancelKeyBase}:${qty}:${reasonKey}`;
		if (!isManager) {
			askManager(
				op,
				fill(
					message(language, 'cancelTitle'),
					qty,
					lineName(language, line.name, line.grams),
					orderNumber(order),
				),
				key,
			);
			return;
		}
		void (async () => {
			cancelSaving = reasonKey;
			try {
				const outcome = await runApproved(op, key, null);
				if (outcome.kind === 'done') {
					cancelling = null;
					return;
				}
				cancelFailure =
					outcome.kind === 'unreachable'
						? message(language, 'offline')
						: outcome.code === 'ALREADY_PAID'
							? message(language, 'ordersPaidAlready')
							: message(language, 'ordersFailed');
			} finally {
				cancelSaving = null;
			}
		})();
	}

	function askManager(op: ApprovedOperation, title: string, key: string) {
		approval.unpick();
		asking = { op, title, key };
		void approvers.load();
	}

	function approved(order: Order) {
		if (asking?.op.kind === 'driver') {
			driverOpen = false;
			driverChanging = null;
		}
		asking = null;
		cancelling = null;
		approval.unpick();
		if (view === 'orders' || order.status !== 'open') void refreshDay();
	}

	function reprint(order: Order) {
		ordersFailure = '';
		void run(`print:${order.id}`, async () => {
			const outcome = await print(order.id);
			if (!outcome) ordersFailure = message(language, 'doneNotPrinted');
		});
	}

	function testPrint() {
		ordersFailure = '';
		void run('test', async () => {
			if (!(await print(null))) ordersFailure = message(language, 'ordersFailed');
		});
	}

	function noSale() {
		void run('nosale', async () => {
			ordersFailure = '';
			const outcome = await openDrawerNoSale();
			if (outcome.kind !== 'opened') {
				ordersFailure = message(
					language,
					outcome.kind === 'refused' && outcome.code === 'NOT_FOUND'
						? 'ordersNoDrawer'
						: 'ordersFailed',
				);
			}
			await refreshDay();
		});
	}

	// ---------- a newer screen, and a newer Bosla app ----------

	// The page's writes on their way, which an install would leave with no
	// answer, because it stops the order store (#925).
	const writes = $derived<Writes>({
		paying: paying !== null,
		retrying,
		pendingSale: pending !== null,
		saving:
			sending ||
			givingDiscount ||
			pendingAction !== null ||
			cancelSaving !== null ||
			approval.phase === 'checking' ||
			mineSaving ||
			tillSetup.saving !== null ||
			tillCashPoint.saving ||
			tillDrivers.saving ||
			tillMesh.turning,
		printing,
		drawer: drawerPhase === 'watching',
		deciding: decisionsPending(),
		addressRemoval: addressRemovalUnsettled(),
	});

	// Under its own lock the till installs over any view, with a cart, customer
	// details, a note or an open order (#925): only a write on its way or a form
	// holding typing that only the page keeps holds it. Done holds nothing; a
	// lock closes Open shift and Close shift, which keep what was typed, and say
	// so with a float or a count, or an open or a close on its way.
	const lockedAtRest = $derived(
		lockedTillAtRest({
			...writes,
			sellSheet: sheet !== null && sheet.name !== 'done',
			formOpen:
				mineOpen ||
				(view === 'printers' && (printerSetupOpen || printerIdentifyOpen)) ||
				tillCashPoint.changed ||
				tillDrivers.typing ||
				openShiftHolding ||
				closeShiftHolding ||
				cashMoveHolding,
			undoRunning: undo !== null || tillDrivers.unsettled,
		}),
	);

	// The till runs only inside the Bosla app, where a newer screen is a signed
	// bundle its order store kept: while the till stays at rest it asks the
	// shell to switch, which reloads it when the shell holds one (#392).
	// Under its own lock with nobody at it (the Lock button, the cashier's
	// name, the idle lock), it asks first to install a newer app update the
	// store kept, while no print is on its way (UC-57; Roman on #650 and #925),
	// and to switch after it only at strict rest; with somebody at the lock it
	// asks nothing.
	const restAsk = $derived(tillAsk(atRest, locked, ownLockIdle, lockedAtRest));
	$effect(() => {
		if (restAsk === 'switch') return askWhileAtRest(switchAtRest);
		if (restAsk === 'nothing') return;
		// Away from an idle Sell the lock asks to install alone.
		const switchAfter = restAsk === 'install' ? switchAtRest : async () => false;
		return askWhileAtRest((stillAtRest) =>
			askAtLock((atRest) => installWhenQuiet(undefined, atRest), switchAfter, stillAtRest),
		);
	});

	// Install now on Orders' ready Notice (#925): once a manager pressed it,
	// the unlocked till asks to install whenever no write is on its way and no
	// form holds typing, at once and again every INSTALL_NOW_ASK_MS while a
	// print is. The sheets a lock closes count too: Discount, Cancel item,
	// Manager approval with its pad, and Pick a table. A lock ends the wait
	// (lockScreen), and the lock's own ask takes over.
	const installNow = new InstallNow();
	const installNowFree = $derived(
		installNowAsks(
			installNow.waiting,
			locked,
			lockedAtRest,
			discountOpen || cancelling !== null || asking !== null || tableOpen || driverOpen,
		),
	);
	$effect(() => {
		if (!installNowFree) return;
		return askWhileAtRest((free) => installNow.ask(free), INSTALL_NOW_ASK_MS);
	});

	// ---------- lock and sign out ----------

	$effect(() => watchActivity());

	// One tick a second, comparing against the last activity. A timer reset on
	// every tap would not fire in a sleeping tab; comparing means the check is
	// right on the tap that wakes it.
	$effect(() => {
		if (locked) return;
		const tick = setInterval(() => {
			if (idleFor(Date.now()) >= idleSeconds * 1000) void idleLock();
		}, 1000);
		return () => clearInterval(tick);
	});

	// The idle lock asks the store first (#382, N18): while the store cannot
	// reach Bosla it holds the lock open, because unlocking again needs Bosla,
	// and the cashier stays signed in; the till asks again at its next check.
	// The rule is the store's. A store that does not say locks as before.
	let askingIdle = false;
	async function idleLock() {
		if (locked || askingIdle) return;
		askingIdle = true;
		try {
			// A driver's removal waiting out its Undo goes before the store locks.
			await tillDrivers.settleBeforeLock();
			const answer = await askIdleLock();
			// 'held': the cashier stays signed in, and the next check asks again.
			if (answer === 'locked') lockScreen();
			else if (answer === 'unknown') await lock();
		} finally {
			askingIdle = false;
		}
	}

	// A tap on Lock, or the cashier's name, always locks.
	async function lock() {
		if (locked) return;
		// A driver's removal waiting out its Undo reaches the store before
		// the lock does, which would refuse it, but never holds the lock long.
		const removing = tillDrivers.settleBeforeLock();
		lockScreen();
		await removing;
		try {
			await api.POST('/api/till/sessions/lock');
		} catch {
			// The screen is locked either way: that is what the person in front
			// of it can see.
		}
	}

	function lockScreen() {
		if (locked) return;
		pickSignedIn();
		discountOpen = false;
		tableOpen = false;
		closeDrivers(true);
		cancelling = null;
		asking = null;
		approval.unpick();
		openShiftOpen = false;
		closeShiftOpen = false;
		cashMoveOpen = false;
		// The next person signed in never sees the last one's drawer line.
		forgetMovementDrawer();
		installNow.cancel();
		// A driver's removal waiting out its Undo goes now: the Undo is gone
		// with the screen.
		void tillDrivers.commitRemoval();
		locked = true;
	}

	async function saveMine() {
		if (mineSaving) return;
		mineSaving = true;
		mineError = '';
		const outcome = await saveMyItems();
		mineSaving = false;
		if (outcome === 'saved') {
			mineOpen = false;
			rail = 'mine';
		} else if (outcome === 'gone') {
			mineError = message(language, 'mineGone');
			void loadMenu();
		} else {
			mineError = message(language, 'ordersFailed');
		}
	}

	async function signOut() {
		forgetMyItems();
		forgetPrinting();
		await tillDrivers.settleBeforeLock();
		try {
			await api.POST('/api/till/sessions/signout');
		} catch {
			// Signed out locally either way, for the same reason as lock().
		}
		onSignedOut();
	}

	// T3's connection line (#382, N14 and N15): the store's own link and its
	// waiting count, shown once above 0 for 5 seconds.
	const connection = $derived(connectionLine(language, isOnline(), linkUp(), shownWaiting()));

	const where = $derived(`${till.account} · ${till.shop}`);
	const languageName = $derived(
		message(
			language,
			language === 'ar'
				? 'settingsLanguageArabic'
				: language === 'msa'
					? 'settingsLanguageMSA'
					: 'settingsLanguageEnglish',
		),
	);
	const themeName = $derived(
		message(
			language,
			themeChoice() === 'light'
				? 'themeLight'
				: themeChoice() === 'dark'
					? 'themeDark'
					: 'themeSystem',
		),
	);
	const payTitle = $derived(
		sheet?.name === 'done'
			? `${message(language, 'doneOrder')} ${sheet.settled.number}`
			: sheet?.name === 'pay' && sheet.collect
				? `${message(language, 'payTakeTitle')} ${sheet.collect.number}`
				: message(language, 'payTitle'),
	);
</script>

<!-- A long press on a touch screen pops the browser's context menu over the
     grid. Cancelled for the whole till, because it is wrong everywhere here. -->
<svelte:window oncontextmenu={(event) => event.preventDefault()} />

<div class="till" data-testid="screen-till">
	<!-- inert while locked: nothing beneath the overlay can be tapped or
	     focused, and none of it unmounts. -->
	<div class="work" data-testid="till-work" inert={locked}>
		<header>
			<BrandMark size={26} />
			<div class="who">
				<span class="shop">{where}</span>
				<span class="till-name">{till.till}</span>
			</div>
			<!-- The signed-in name shows whole; a name the row cannot hold, with
			     the shop and till names at their 120 px, is cut at its end (T3). -->
			<span class="who-name">
				<Button
					testId="till-who"
					truncate
					icon="user"
					label={session.userName}
					tone="text"
					size="sm"
					onPress={() => void lock()}
				/>
			</span>
			<Button
				testId="till-orders"
				icon="receipt"
				label={message(language, 'ordersTitle')}
				tone="secondary"
				size="sm"
				onPress={() => void showOrders()}
			/>
			{#if openCount() > 0}
				<Button
					testId="till-open-count"
					figure={openCount()}
					label={message(language, 'headerOpenCount')}
					ariaLabel={fill(message(language, 'headerOpenCountAria'), openCount())}
					tone="secondary"
					size="sm"
					onPress={() => void showOrders('open')}
				/>
			{/if}
			<Button
				testId="till-settings"
				icon="settings"
				label={message(language, 'tillSettings')}
				tone="secondary"
				size="sm"
				onPress={() => {
					// Back returns where Till settings came from; T25 opened from
					// Till settings already returns to it.
					if (view !== 'settings' && !(view === 'printers' && printersFrom === 'settings')) {
						settingsFrom = view;
						settingsPrintersFrom = printersFrom;
					}
					view = 'settings';
					if (isManager) void tillSetup.read();
				}}
			/>
			<Button
				testId="till-lock"
				icon="lock"
				label={message(language, 'lockLockNow')}
				tone="secondary"
				size="sm"
				onPress={() => void lock()}
			/>
			<Button
				testId="till-signout"
				label={message(language, 'lockSignOut')}
				tone="text-quiet"
				size="sm"
				onPress={() => void signOut()}
			/>
		</header>

		<!-- A standing condition takes space in the page rather than floating
		     over it, and leaves when the condition does. -->
		{#if connection.kind === 'storeDown'}
			<Notice life="standing" tone="flag" icon="offline" testId="till-offline">
				{message(language, 'offline')}
			</Notice>
		{:else if connection.kind === 'netDown'}
			<!-- The store sells on with the internet down (#382, N15): the line
			     says so, and how many sales wait for Bosla. -->
			<Notice life="standing" tone="neutral" icon="offline" testId="till-net-down">
				{connection.text}
			</Notice>
		{:else if connection.kind === 'waiting'}
			<Notice life="standing" tone="neutral" icon="clock" testId="till-waiting">
				{connection.text}
			</Notice>
		{/if}
		{#if notice && sheet === null && view === 'sell'}
			<Notice life="standing" tone="flag" testId="till-sale-error">{notice}</Notice>
		{/if}
		{#if sheet === null}
			{#each printNotices() as pNotice (pNotice.key)}
				{#if pNotice.kind === 'held'}
					<!-- Held for a decision (#515): Decide opens Orders, whose list of
					     prints waiting for a decision heads it. -->
					<Notice
						life="standing"
						tone="flag"
						testId="till-print-notice-{pNotice.key}"
						action={message(language, 'printDecide')}
						actionTestId="till-print-notice-decide-{pNotice.key}"
						onAction={() => void showOrders()}
					>
						{noticeText(pNotice)}
					</Notice>
				{:else}
					<Notice
						life="standing"
						tone="flag"
						testId="till-print-notice-{pNotice.key}"
						action={message(language, 'printNoticeAgain')}
						actionTestId="till-print-notice-again-{pNotice.key}"
						actionBusy={printNoticeBusy(pNotice.key)}
						onAction={() => printNoticeAgainHere(pNotice)}
					>
						{noticeText(pNotice)}
					</Notice>
				{/if}
			{/each}
		{/if}

		{#if shiftNotice && sheet?.name !== 'pay'}
			<!-- While the till takes no payment (UC-60). Payment shows its own. -->
			<Notice
				life="standing"
				tone="flag"
				testId="till-shift-notice"
				action={shiftNotice.action ?? undefined}
				actionTestId="till-shift-open"
				onAction={shiftNotice.action ? () => (openShiftOpen = true) : undefined}
			>
				{shiftNotice.text}
			</Notice>
		{/if}
		<div class="body">
			{#if view === 'orders'}
				{#key ordersTap}
					<Orders
						{language}
						{minorDigits}
						{day}
						loading={dayLoading && day === null}
						loadFailed={dayFailed}
						pending={pendingAction ?? (printing ? 'print' : null)}
						{slow}
						failure={ordersFailure}
						{voidClosedFor}
						{sends}
						startFilter={ordersStart}
						hasReceiptPrinter={!printersRead() || receiptPrinter() !== null}
						{isManager}
						installWaiting={installNow.waiting}
						onInstallNow={() => installNow.press()}
						held={heldRows}
						onDecide={decide}
						{decideFailure}
						onShift={() => (view = 'shift')}
						onPrinters={() => {
							printersFrom = 'orders';
							view = 'printers';
						}}
						onReprint={reprint}
						canOpen={draft.lines.length === 0}
						onOpen={openOnSell}
						onVoid={void_}
						onChangeDriver={openDriverChange}
						onOpenDrawer={noSale}
						onTestPrint={testPrint}
						onRetry={() => void refreshDay()}
						onBack={() => (view = 'sell')}
					/>
				{/key}
			{:else if view === 'shift'}
				<Shift
					{language}
					{minorDigits}
					onOpenShift={() => (openShiftOpen = true)}
					onCloseShift={() => (closeShiftOpen = true)}
					onCashMovement={() => (cashMoveOpen = true)}
					onBack={() => (view = 'orders')}
					closing={closeShiftOpen}
				/>
			{:else if view === 'settings'}
				<TillSettings
					{language}
					{isManager}
					display={`${languageName} · ${themeName}`}
					onDisplay={() => (displayOpen = true)}
					driverCount={tillDrivers.readOk ? tillDrivers.shown.length : null}
					driversForbidden={tillDrivers.forbidden}
					onDrivers={() => {
						void tillDrivers.read();
						driversOpen = true;
					}}
					setup={tillSetup}
					context={setupContext}
					onBack={() => {
						if (settingsFrom === 'printers') printersFrom = settingsPrintersFrom;
						view = settingsFrom;
					}}
					onOpen={(open) => {
						if (open === 'printers') openSetupPrinters('settings');
						else if (open === 'sharing') sharingOpen = true;
						else if (open === 'cash_point') {
							tillCashPoint.reset();
							void tillCashPoint.read();
							cashPointOpen = true;
						}
					}}
				/>
			{:else if view === 'printers'}
				<Printers
					{language}
					printers={printers()}
					{isManager}
					notAnswering={notAnsweringPurposes()}
					setup={meshSetup()}
					capabilities={meshCapabilities()}
					sharing={meshSharing()}
					onActivate={activateLocalSetup}
					identifying={identifyingIds()}
					onIdentify={(p) => {
						printerIdentifyTarget = p;
						printerIdentifyOpen = true;
					}}
					onAdd={() => {
						printerSetupMode = 'add';
						printerSetupTarget = null;
						printerSetupOpen = true;
					}}
					onChange={(p) => {
						printerSetupMode = 'change';
						printerSetupTarget = p;
						printerSetupOpen = true;
					}}
					onSetUpAgain={(p) => {
						printerSetupMode = 'again';
						printerSetupTarget = p;
						printerSetupOpen = true;
					}}
					onBack={() => {
						view = printersFrom === 'onboarding' ? 'sell' : printersFrom;
					}}
				/>
			{:else}
				<Sell
					{language}
					{menu}
					menuLoaded={hasMenu()}
					{minorDigits}
					deliveryFeeMinor={areaFeeMinor}
					{areas}
					countryCode={till.settings.defaultCountryCode}
					pending={pending !== null || additionPending()}
					{retrying}
					openOrder={open}
					today={currentTradingDay()}
					{sending}
					{sendSlow}
					onSend={() => void send()}
					onNewOrder={newOrder}
					paying={sheet?.name === 'pay' || sheet?.name === 'done'}
					onAsk={ask}
					onEditLine={editLine}
					onRemoveLine={removeFromCart}
					onOpenDelivery={openDelivery}
					onPay={openPay}
					onPrintBill={bill}
					printingBill={pendingAction?.startsWith('bill:') ?? false}
					billChanged={open?.billChangedSincePrinted ?? false}
					{billHeld}
					onRetryPending={() => void retryPending()}
					mine={myItems()}
					selected={rail}
					onSelect={(entry) => (rail = entry)}
					onEditMine={() => {
						mineError = '';
						mineOpen = true;
					}}
					{isManager}
					onDiscount={() => (discountOpen = true)}
					onCancelLine={openCancel}
					driverName={draft.type === 'delivery' ? (cartDriver()?.name ?? '') : ''}
					{driverNotice}
					onOpenDrivers={() => openDrivers(false)}
					tableName={draft.type === 'dine_in' ? (cartTable()?.name ?? '') : ''}
					tableAsk={tableNeeded()}
					{tableNotice}
					onDineIn={dineIn}
					onOpenTables={openTables}
				/>
			{/if}
		</div>

		<GiveDiscount
			open={discountOpen && sheet === null && view === 'sell'}
			{language}
			{minorDigits}
			baseMinor={open
				? open.subtotalMinor + open.deliveryFeeMinor
				: (totals?.subtotalMinor ?? 0) + (totals?.deliveryMinor ?? 0)}
			discounts={menu.discounts ?? []}
			current={open ? (open.discount ?? null) : draft.discount}
			{isManager}
			onPick={(discount) => void giveDiscount(discount)}
			onClose={() => {
				if (!givingDiscount) discountOpen = false;
			}}
		/>

		<CancelLine
			open={cancelling !== null &&
				asking === null &&
				sheet === null &&
				view === 'sell' &&
				open !== null}
			{language}
			line={cancelling}
			opened={cancelOpened}
			sentCount={(open?.lines ?? []).reduce((n, line) => n + line.qty, 0)}
			{isManager}
			saving={cancelSaving}
			failure={cancelFailure}
			onReason={cancelReason}
			onClose={() => {
				if (cancelSaving === null) cancelling = null;
			}}
		/>

		<OpenShift
			open={openShiftOpen}
			{language}
			{minorDigits}
			level={sheet?.name === 'pay' ? 2 : 1}
			parentTitle={payTitle}
			onHolding={(holding) => (openShiftHolding = holding)}
			onClose={() => (openShiftOpen = false)}
		/>

		<CashMovement
			open={cashMoveOpen && view === 'shift'}
			{language}
			{minorDigits}
			onHolding={(holding) => (cashMoveHolding = holding)}
			onClose={() => (cashMoveOpen = false)}
		/>

		<CloseShift
			open={closeShiftOpen && view === 'shift'}
			{language}
			{minorDigits}
			{isManager}
			tillName={till.till}
			userId={session.userId ?? ''}
			onHolding={(holding) => (closeShiftHolding = holding)}
			onClose={() => (closeShiftOpen = false)}
		/>

		<ManagerApproval
			open={asking !== null}
			{language}
			title={asking?.title ?? ''}
			people={approvers.cashiers}
			status={approvers.status}
			entry={approval}
			onClose={() => (asking = null)}
		/>

		<PickDriver
			open={driverOpen &&
				asking === null &&
				sheet === null &&
				(driverChanging ? view === 'orders' : view === 'sell')}
			{language}
			drivers={currentDrivers()}
			read={driversRead()}
			current={driverChanging ? (driverChanging.driverId ?? '') : draft.driverId}
			title={driverChanging
				? fill(message(language, 'driverChangeTitle'), orderNumber(driverChanging))
				: message(language, 'sellPickDriver')}
			changing={driverChanging !== null}
			{isManager}
			saving={driverSaving}
			failure={driverFailure}
			onPick={pickDriver}
			onRetry={() => void readDrivers()}
			onDrivers={() => {
				// A manager opens the shop's drivers (T34) at once, over Till
				// settings, as its Drivers row does; Back from Till settings
				// returns to Sell, where the delivery waits for its Send.
				closeDrivers();
				if (view !== 'settings') {
					settingsFrom = view;
					settingsPrintersFrom = printersFrom;
				}
				view = 'settings';
				void tillSetup.read();
				void tillDrivers.read();
				driversOpen = true;
			}}
			onClose={closeDrivers}
		/>

		<PickTable
			open={tableOpen && sheet === null && view === 'sell' && shopHasTables()}
			{language}
			tables={currentTables()}
			read={tablesRead()}
			current={draft.tableId}
			onPick={pickTable}
			onRetry={() => void readTables()}
			onClose={() => (tableOpen = false)}
		/>

		<SetUpTill
			open={setupOpen}
			{language}
			setup={tillSetup}
			index={setupIndex}
			context={setupContext}
			printers={printers()}
			mesh={tillMesh}
			cashPoint={tillCashPoint}
			selfId={till.tillId}
			onBack={() => {
				// Back from the cash-point step drops its pick, as Close does.
				if (knownSteps(tillSetup.setup)[setupIndex]?.id === 'cash_point') tillCashPoint.reset();
				setupIndex = Math.max(0, setupIndex - 1);
				tillSetup.failure = false;
			}}
			onPass={(passed) => void passSetup(passed)}
			onClose={() => {
				setupClosedFor = session.userId ?? '';
				tillCashPoint.reset();
			}}
			onPrinters={() => openSetupPrinters('onboarding')}
		/>

		<Sheet
			open={sharingShows}
			level={1}
			title={message(language, 'sharingTitle')}
			onClose={() => (sharingOpen = false)}
			closeLabel={message(language, 'sheetClose')}
			closeTestId="sharing-close"
			testId="sharing-sheet"
		>
			<SharingStep {language} mesh={tillMesh} heading={false} />
		</Sheet>

		<Sheet
			open={cashPointShows}
			level={1}
			title={message(language, 'cashPointTitle')}
			onClose={closeCashPoint}
			closeLabel={message(language, 'sheetClose')}
			closeTestId="cash-point-close"
			primaryLabel={message(language, 'confirm')}
			primaryIcon="check"
			primaryState={tillCashPoint.savingSlow ? 'pending' : 'idle'}
			pendingLabel={message(language, 'saving')}
			primaryDisabled={!tillCashPoint.changed || !tillCashPoint.ready || tillCashPoint.saving}
			primaryTestId="cash-point-confirm"
			onPrimary={() => void confirmCashPoint()}
			locked={tillCashPoint.saving}
			testId="cash-point-sheet"
		>
			<CashPointStep
				{language}
				cashPoint={tillCashPoint}
				mesh={tillMesh}
				selfId={till.tillId}
				heading={false}
			/>
		</Sheet>

		<Sheet
			open={driversShows}
			level={1}
			title={message(language, 'tillDriversTitle')}
			onClose={() => (driversOpen = false)}
			closeLabel={message(language, 'sheetClose')}
			closeTestId="till-drivers-close"
			locked={tillDrivers.saving}
			testId="till-drivers-sheet"
		>
			<TillDriversPanel {language} drivers={tillDrivers} offline={!linkUp()} />
		</Sheet>

		{#if tillDrivers.pendingRemoval && !locked}
			<!-- Over Drivers: a removed driver, until his Undo runs out and the
			     server hears of it. -->
			{#key tillDrivers.pendingRemoval}
				<Notice
					life="deadline"
					tone="neutral"
					action={message(language, 'sellUndo')}
					actionTestId="till-driver-undo"
					onAction={() => tillDrivers.undoRemoval()}
					onDismiss={() => void tillDrivers.commitRemoval()}
					testId="till-driver-undo-notice"
				>
					{fill(message(language, 'tillDriversRemoved'), tillDrivers.pendingRemoval.name)}
				</Notice>
			{/key}
		{/if}

		<PrinterSetup
			open={printerSetupOpen && sheet === null && view === 'printers'}
			{language}
			mode={printerSetupMode}
			printer={printerSetupTarget}
			onClose={() => (printerSetupOpen = false)}
			onSaved={() => {
				printerSetupOpen = false;
			}}
		/>

		<PrinterIdentify
			open={printerIdentifyOpen && sheet === null && view === 'printers'}
			{language}
			printer={printerIdentifyTarget}
			onClose={() => (printerIdentifyOpen = false)}
			onSaved={() => {
				printerIdentifyOpen = false;
			}}
		/>

		<Sheet
			open={mineOpen}
			title={message(language, 'mineEdit')}
			primaryLabel={message(language, 'confirm')}
			primaryIcon="check"
			primaryTestId="mine-done"
			primaryState={mineSaving ? 'pending' : 'idle'}
			pendingLabel={message(language, 'mineSaving')}
			locked={mineSaving}
			onPrimary={() => void saveMine()}
			onClose={() => (mineOpen = false)}
			closeLabel={message(language, 'sheetClose')}
			closeTestId="mine-close"
			testId="mine-sheet"
		>
			<MyItems
				{language}
				{menu}
				picks={currentPicks()}
				error={mineError}
				onChange={(next) => {
					mineError = '';
					setPicks(next);
				}}
			/>
		</Sheet>

		<Sheet
			open={displayOpen}
			title={message(language, 'tillDisplayTitle')}
			primaryLabel={message(language, 'confirm')}
			primaryIcon="check"
			primaryTestId="display-done"
			onPrimary={() => (displayOpen = false)}
			onClose={() => (displayOpen = false)}
			closeLabel={message(language, 'sheetClose')}
			closeTestId="display-close"
			testId="till-display-sheet"
		>
			<div class="display">
				<div class="pick">
					<span class="pick-label">{message(language, 'settingsLanguage')}</span>
					<Segmented
						label={message(language, 'settingsLanguage')}
						options={[
							{
								value: 'ar',
								label: message(language, 'settingsLanguageArabic'),
								testId: 'till-language-ar',
							},
							{
								value: 'msa',
								label: message(language, 'settingsLanguageMSA'),
								testId: 'till-language-msa',
							},
							{
								value: 'en',
								label: message(language, 'settingsLanguageEnglish'),
								testId: 'till-language-en',
							},
						]}
						value={languageChoice() ?? language}
						onChange={(picked) => setLanguageChoice(picked as Language)}
					/>
				</div>
				<div class="pick">
					<span class="pick-label">{message(language, 'settingsTheme')}</span>
					<Segmented
						label={message(language, 'settingsTheme')}
						options={[
							{
								value: 'light',
								label: message(language, 'themeLight'),
								testId: 'till-theme-light',
							},
							{ value: 'dark', label: message(language, 'themeDark'), testId: 'till-theme-dark' },
							{
								value: 'system',
								label: message(language, 'themeSystem'),
								testId: 'till-theme-system',
							},
						]}
						value={themeChoice()}
						onChange={(picked) => setThemeChoice(picked as ThemeChoice)}
					/>
				</div>
				<p class="display-note">{message(language, 'tillDisplayNote')}</p>
			</div>
		</Sheet>

		{#if undo}
			<!-- Keyed, so a second removal starts a fresh ten seconds. -->
			{#key undo}
				<Notice
					life="deadline"
					tone="neutral"
					action={message(language, 'sellUndo')}
					actionTestId="cart-undo"
					onAction={undoRemove}
					onDismiss={() => (undo = null)}
				>
					{message(language, 'sellRemoved')}: {lineName(
						language,
						undo.line.name,
						undo.line.weight?.grams,
					)}
				</Notice>
			{/key}
		{/if}

		<Sheet
			open={sheet?.name === 'editor' && work !== null}
			title={work?.name ?? ''}
			meta={work ? formatMinor(unitOf(menu, work), minorDigits) : undefined}
			metaTestId="editor-unit"
			primaryLabel={message(language, 'confirm')}
			primaryIcon="check"
			primaryTestId="editor-done"
			primaryDisabled={!work || blocked(menu, work)}
			onPrimary={finishEdit}
			secondaryLabel={work && work.key !== '' ? message(language, 'menuRemoveItem') : undefined}
			secondaryTestId="editor-remove"
			onSecondary={removeEdited}
			onClose={() => (sheet = null)}
			closeLabel={message(language, 'sheetClose')}
			closeTestId="editor-close"
		>
			{#if work}
				<LineEditor
					{language}
					{menu}
					{minorDigits}
					{work}
					shortcuts={shortcuts?.itemId === work.itemId ? shortcuts.words : []}
					onChange={(next) => (work = next)}
				/>
			{/if}
		</Sheet>

		<Sheet
			open={sheet?.name === 'delivery'}
			title={dlTitle}
			titleTestId="delivery-title"
			backLabel={dl.step === 'where' && dl.addrNew && (dl.customer?.addresses.length ?? 0) > 0
				? message(language, 'customerSaved')
				: message(language, 'deliveryPhone')}
			onBack={dl.step === 'phone' ? undefined : stepBack}
			backTestId="delivery-back"
			meta={dlFee}
			metaLabel={message(language, 'sellDelivery')}
			metaTestId="delivery-fee"
			primaryLabel={dl.step === 'phone' ? undefined : message(language, 'confirm')}
			primaryIcon="check"
			primaryTestId="delivery-done"
			primaryDisabled={!dlValues}
			onPrimary={finishDelivery}
			secondaryLabel={dl.step === 'phone'
				? message(language, 'deliveryNewCustomer')
				: dlOnSaved
					? message(language, 'deliveryNewAddress')
					: undefined}
			secondaryTestId={dl.step === 'phone' ? 'delivery-new-customer' : 'delivery-new-address'}
			secondaryDisabled={dl.step === 'phone' && !canStartNew(dlContext)}
			onSecondary={dl.step === 'phone' ? startNewCustomer : startNewAddress}
			onClose={closeDelivery}
			closeLabel={message(language, 'sheetClose')}
			closeTestId="delivery-close"
			covered={customerOpen}
		>
			<Delivery
				{language}
				{areas}
				{minorDigits}
				{countryCode}
				shopFeeMinor={areaIds.length === 0 ? quotedFee : null}
				canEditCustomer={session.role === 'manager'}
				online={isOnline() && linkUp()}
				onEditCustomer={() => (customerOpen = true)}
				driverName={cartDriver()?.name ?? ''}
			/>
		</Sheet>

		<CustomerSheet
			open={sheet?.name === 'delivery' && customerOpen}
			{language}
			customer={pickedPhone() ? knownCustomer(pickedPhone()) : null}
			{areas}
			{minorDigits}
			onBack={() => (customerOpen = false)}
			onRenamed={(before, after) => {
				// A confirmed order follows the correction unless another name was typed.
				const typed = draft.customerName.trim();
				if (typed === before) setDelivery({ customerName: after });
			}}
			onGone={() => {
				customerOpen = false;
				customerGone(countryCode);
			}}
		/>

		{#if pendingAddressRemoval()}
			<!-- Over the customer sheet: a manager's removed address, until its
			     Undo runs out and the server hears of it. -->
			{#key pendingAddressRemoval()}
				<Notice
					life="deadline"
					tone="neutral"
					action={message(language, 'sellUndo')}
					actionTestId="address-undo"
					onAction={undoAddressRemoval}
					onDismiss={async () => {
						if ((await commitAddressRemoval()) === 'gone') {
							customerOpen = false;
							customerGone(countryCode);
						}
					}}
					testId="address-undo-notice"
				>
					{message(language, 'customerAddressRemoved')}
					{pendingAddressRemoval()?.address}
				</Notice>
			{/key}
		{/if}

		<!-- Payment steps aside while Manager approval asks for its card payment
		     (UC-16.R9), as Close shift does, and comes back on its Close. -->
		<Sheet
			open={(sheet?.name === 'pay' || sheet?.name === 'done') && asking?.op.kind !== 'pay'}
			title={payTitle}
			titleTestId={sheet?.name === 'done' ? 'done-number' : 'pay-title'}
			primaryLabel={sheet?.name === 'done'
				? message(language, 'doneNext')
				: message(language, 'confirm')}
			primaryTestId={sheet?.name === 'done' ? 'done-next' : 'pay-confirm'}
			primaryIcon={sheet?.name === 'done' ? undefined : 'check'}
			covered={openShiftOpen}
			primaryDisabled={sheet?.name === 'pay' && (!tenderReady || shiftNotice !== null)}
			primaryState={paying === 'confirm' ? 'pending' : 'idle'}
			pendingLabel={message(language, 'payFinishing')}
			onPrimary={sheet?.name === 'done' ? nextSale : confirmTender}
			locked={paying !== null}
			onClose={() => {
				if (sheet?.name === 'done') {
					nextSale();
				} else if (sheet?.name === 'pay' && sheet.collect) {
					sheet = null;
					tender = '';
				} else {
					sheet = null;
				}
			}}
			closeLabel={message(language, 'sheetClose')}
			closeTestId="pay-close"
		>
			{#if sheet?.name === 'pay'}
				<Payment
					{shiftNotice}
					onOpenShift={() => (openShiftOpen = true)}
					{language}
					{minorDigits}
					totalMinor={payTotal}
					quickTenderSteps={till.settings.quickTenderSteps ?? []}
					typed={tender}
					onType={(next) => (tender = next)}
					working={paying}
					failure={saleFailure}
					onCash={(tenderedMinor) => settleCash(tenderedMinor, 'exact')}
					onCard={settleCard}
					discounted={sheet.collect ? sheet.collect.discounted : (totals?.discountMinor ?? 0) > 0}
					cardAsksManager={!isManager && !!sheet.collect?.cardNeedsManager}
				/>
			{:else if sheet?.name === 'done'}
				{#key sheet.settled.orderId + sheet.settled.number}
					<Done
						{language}
						{minorDigits}
						method={sheet.settled.method}
						changeMinor={sheet.settled.changeMinor}
						totalMinor={sheet.settled.totalMinor}
						phase={printSlow || printPhase === 'failed' || printPhase === 'uncertain'
							? printPhase
							: 'idle'}
						free={sheet.settled.free ?? null}
						noReceiptPrinter={printersRead() && receiptPrinter() === null}
						noDrawer={sheet.settled.method === 'cash' &&
							!sheet.settled.free &&
							!sheet.settled.drawerJobId &&
							sheet.settled.orderId !== '' &&
							printersRead() &&
							receiptPrinter() !== null}
						{drawerPhase}
						onDrawerRetry={() => void retryKick()}
						{drawerNow}
						onDrawerNow={() => void openNow()}
						{drawerRefusal}
						{printing}
						onPrint={() => {
							if (sheet?.name === 'done' && sheet.settled.orderId) {
								void print(sheet.settled.orderId);
							}
						}}
						onPrintAnotherCopy={() => void anotherCopy()}
						refusal={receiptRefusal}
					/>
				{/key}
			{/if}
		</Sheet>
	</div>

	{#if locked}
		<div class="overlay" data-testid="till-lock-overlay">
			<LockPad
				{language}
				where="{till.account} · {till.shop} · {till.till}"
				whoTitle={message(language, 'lockWhoContinues')}
				title={message(language, 'lockScreenLocked')}
				{entry}
				{minorDigits}
				whereTestId="till-lock-where"
				dotsTestId="till-lock-dots"
				messageTestId="till-lock-error"
				padTestId="till-pad"
				pickTestId="till-lock"
			/>
		</div>
	{/if}
</div>

<style>
	/* The till's own width sizes the rail and the cart (--till-rail and
	   --till-cart are in cqw), and a till is touch, so every control grows to
	   the tap floor once, here. */
	.till {
		--ctl-min: var(--tap-min);
		--notice-inset-start: 0px;
		position: relative;
		container-type: inline-size;
		block-size: 100dvh;
		background: var(--surface-page);
	}

	@media (max-height: 600px) {
		.till {
			--key-h: var(--key-h-short);
			--sheet-inset: var(--sheet-inset-short);
		}
	}

	.work {
		block-size: 100%;
		display: flex;
		flex-direction: column;
		min-block-size: 0;
	}

	header {
		flex: none;
		block-size: var(--till-bar-h);
		display: flex;
		align-items: center;
		gap: var(--space-5);
		padding: 0 var(--space-7);
		background: var(--surface-card);
		border-block-end: var(--border-width) solid var(--border-default);
	}

	/* The shop and till names take the row's free space, and once the row is
	   full they stay at 120 px, frozen there with no basis to shrink from,
	   while the signed-in name gives way (T3). */
	.who {
		flex: 1 1 0;
		min-inline-size: 120px;
		overflow: hidden;
		display: flex;
		flex-direction: column;
	}

	.shop,
	.till-name {
		min-inline-size: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	header > :global(button) {
		flex-shrink: 0;
	}
	.who-name {
		display: flex;
		flex: 0 1 auto;
		min-inline-size: 0;
	}

	.shop {
		font-size: var(--text-sm);
		font-weight: 700;
		white-space: nowrap;
	}

	.till-name {
		font-size: var(--text-micro);
		color: var(--text-muted);
		white-space: nowrap;
	}

	.body {
		flex: 1;
		min-block-size: 0;
		display: flex;
	}

	.display {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.pick {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.pick-label {
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	.display-note {
		margin: 0;
		max-inline-size: var(--measure);
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	/* Above every sheet and the undo notice: nothing of the work shows
	   through a locked till. */
	.overlay {
		position: fixed;
		inset: 0;
		z-index: calc(var(--z-notice) + 10);
		display: flex;
		background: var(--surface-page);
	}
</style>
